import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * i18n dictionary (PT-BR + EN-US).
 *
 * Conventions:
 *  - Keys use dot.namespaces (page/component → field). Keep them stable, never localized.
 *  - When a key is missing, `t()` falls back to the key string itself (useful for debugging).
 *  - Common reusable bits live under `common.*` and `status.*`.
 *  - Page/feature namespaces: nav, landing, login, dash, pto, reimb, travel, approvals, admin,
 *    qbo, drafts, notif, header, profile, docs, directory, org.
 */
const dict = {
  pt: {
    // ─── App & landing
    "app.name": "Welcome Home",
    "app.tagline": "Seu portal de pessoas. Tudo em um só lugar.",
    "app.cta": "Entrar no portal",
    "landing.badge": "Pessoas & Organização",
    "landing.feat_pto": "PTO & Férias",
    "landing.feat_pto_desc": "Solicite, acompanhe e aprove tudo em um único fluxo.",
    "landing.feat_reimb": "Reembolsos",
    "landing.feat_reimb_desc": "Overhead e Project Costs com invoice automática.",
    "landing.feat_dir": "Diretório",
    "landing.feat_dir_desc": "Encontre seus colegas, departamentos e gestores.",
    "landing.feat_org": "Organograma",
    "landing.feat_org_desc": "Visualize a estrutura completa da sua empresa.",
    "landing.footer": "Portal de Pessoas & Organização",
    // ─── Navigation / sidebar
    "nav.dashboard": "Início",
    "nav.org": "Organograma",
    "nav.directory": "Diretório",
    "nav.pto": "Férias & PTO",
    "nav.reimb": "Reembolsos",
    "nav.timesheet": "Apontamento de horas",
    "nav.travel": "Viagens",
    "nav.expenses": "Despesas",
    "nav.loans": "Empréstimos",
    "nav.docs": "Documentos",
    "nav.approvals": "Aprovações",
    "nav.profile": "Meu Perfil",
    "nav.admin": "Admin & RH",
    "nav.manual": "Manual do usuário",
    "nav.reports": "Central de Relatórios",
    "reports.page_sub": "Solicite, aprove e baixe relatórios dos dados do portal",
    "reports.tab_mine": "Meus relatórios",
    "reports.tab_approvals": "Aprovações",
    "reports.tab_governance": "Governança",
    "reports.new_request": "Nova solicitação",
    "reports.status_pending_approval": "Aguardando RH",
    "reports.status_approved": "Ativo",
    "reports.status_rejected": "Rejeitado",
    "reports.status_revoked": "Revogado",
    "reports.status_expired": "Expirado",
    "reports.freq_once": "Pontual (uma vez)",
    "reports.freq_weekly": "Semanal",
    "reports.freq_monthly": "Mensal (dia 1º, 5h)",
    "reports.freq_quarterly": "Trimestral (1º dia do quarter)",
    "reports.freq_yearly": "Anual (1º de janeiro)",
    "reports.validity_once": "Uso pontual (disponível por 30 dias)",
    "reports.validity_months": "Por meses",
    "reports.validity_years": "Por anos",
    "reports.validity_permanent": "Permanente",
    "reports.run_now": "Gerar agora",
    "reports.download_xlsx": "Excel",
    "reports.download_csv": "CSV",
    "reports.next_run": "Próxima execução",
    "reports.expires": "Expira em",
    "reports.never_expires": "Sem expiração",
    "reports.runs_history": "Execuções",
    "reports.no_reports": "Nenhum relatório ainda. Clique em \"Nova solicitação\" para pedir o primeiro.",
    "reports.no_pending": "Nenhuma solicitação pendente.",
    "reports.step_entities": "O que você precisa?",
    "reports.step_fields": "Quais colunas?",
    "reports.step_filters": "Filtros & período",
    "reports.step_schedule": "Frequência & validade",
    "reports.step_review": "Revisão & justificativa",
    "reports.wizard_title": "Solicitar novo relatório",
    "reports.justification": "Justificativa (para que você precisa?)",
    "reports.title_label": "Nome do relatório",
    "reports.title_ph": "Ex.: Headcount por departamento",
    "reports.mode_detail": "Detalhado",
    "reports.mode_summary": "Resumo (agrupado)",
    "reports.group_by": "Agrupar por",
    "reports.metrics": "Métricas",
    "reports.sens_restricted": "Restrito",
    "reports.sens_confidential": "Confidencial",
    "reports.confidential_warning": "Este pedido inclui dados confidenciais — o RH precisará aprovar explicitamente.",
    "reports.approve": "Aprovar",
    "reports.reject": "Rejeitar",
    "reports.revoke": "Revogar",
    "reports.preview": "Ver amostra (até 10 linhas)",
    "reports.removed_fields_note": "Colunas removidas pelo RH",
    "reports.requester": "Solicitante",
    "reports.approved_by": "Aprovado por",
    "reports.rejected_reason": "Motivo da rejeição",
    "reports.notif_pending_title": "Solicitação de relatório para aprovar",
    "reports.notif_approved_title": "Seu relatório foi aprovado",
    "reports.notif_rejected_title": "Seu relatório foi rejeitado",
    "reports.notif_run_ready_title": "Nova execução de relatório pronta",
    "reports.save_as_template": "Salvar como template",
    "reports.save_as_template_hint": "O relatório aprovado (com as colunas que você mantiver) vira um template que outros solicitantes podem usar como ponto de partida.",
    "reports.template_name": "Nome do template",
    "reports.template_description": "Descrição (opcional)",
    "reports.template_applied": "Template aplicado — ajuste o que precisar antes de enviar.",
    "reports.template_delete_confirm": "Excluir este template? Solicitações já feitas com base nele não são afetadas.",
    "reports.start_from_template": "Começar a partir de um template",
    "reports.start_from_template_hint": "Templates são relatórios já aprovados pelo RH — clique para pré-preencher o wizard.",
    "reports.global_filters": "Filtros gerais",
    "reports.global_filters_hint": "Aplicam-se automaticamente às entidades que têm o campo correspondente (departamento, role, data).",
    "reports.applied": "aplicado(s)",
    "reports.period": "Período",
    "reports.period_none": "Sem filtro de período",
    "reports.period_custom": "Datas customizadas",
    "reports.departments": "Departamentos",
    "reports.no_departments": "Nenhum departamento cadastrado ainda.",
    "reports.roles": "Roles",
    "reports.err_title_required": "Dá um nome pro relatório.",
    "reports.err_entity_required": "Selecione pelo menos uma entidade.",
    "reports.err_no_fields": "Selecione ao menos uma coluna.",
    "reports.err_no_groupby": "No modo resumo, escolha um campo para agrupar.",
    "reports.showing_of_total": "mostrando {n} de {total}",
    "reports.preview_empty_hint": "Os filtros do relatório retornaram 0 linhas. Verifique se o período ou a lista de departamentos/roles está muito restritiva, ou se ainda não há dados nesse recorte.",
    "reports.date_fixed": "Período fixo",
    "reports.date_rolling": "Janela móvel",
    "reports.date_field": "Campo de data",
    "reports.no_filters": "Sem filtros — traz tudo",
    "reports.window_last_7d": "Últimos 7 dias",
    "reports.window_last_30d": "Últimos 30 dias",
    "reports.window_last_90d": "Últimos 90 dias",
    "reports.window_current_month": "Mês atual",
    "reports.window_previous_month": "Mês anterior",
    "reports.window_current_quarter": "Quarter atual",
    "reports.window_previous_quarter": "Quarter anterior",
    "reports.window_ytd": "Ano até hoje (YTD)",
    "reports.window_previous_year": "Ano anterior",
    "reports.submit_request": "Enviar para aprovação do RH",
    "reports.rows": "linhas",
    "reports.next": "Próximo",
    "reports.back": "Anterior",
    "reports.comment": "Comentário",
    "reports.pending_hint": "Sua solicitação está com o RH. Você será avisado quando for decidida.",
    "reports.frequency": "Frequência",
    "reports.validity": "Validade do acesso",
    "reports.trigger_scheduled": "Agendado",
    "reports.trigger_on_demand": "Sob demanda",
    "reports.trigger_approval": "Na aprovação",
    "reports.gov_intro": "Todos os acessos a relatórios concedidos nesta empresa.",
    "reports.select_fields_hint": "Campos confidenciais e restritos vêm desmarcados por padrão.",
    "reports.weekday_label": "Dia da semana",
    "reports.run_failed": "Falha na geração — gere novamente",
    "reports.restricted": "Você não tem permissão para solicitar relatórios. Fale com o RH para liberar o acesso.",
    "nav.cockpit": "Cockpit do gestor",
    "nav.logout": "Sair",
    "nav.section.workspace": "Meu espaço",
    "nav.section.people": "Pessoas",
    "nav.section.operations": "Operações",
    "nav.section.system": "Sistema",
    "nav.section.admin": "Administração",
    "nav.knowledge": "Base de conhecimento",
    "nav.copilot": "Sage Copilot",
    "nav.performance": "Desempenho",
    "nav.hiring": "Contratações",
    "nav.offboarding": "Desligamentos",
    "nav.salary": "Faixa salarial",
    "nav.planner": "Agenda",
    "nav.dept_hub": "Hub de departamentos",
    "nav.alerts": "Alertas",
    "nav.notifications": "Notificações",
    // ─── Login
    "login.title": "Bem-vindo de volta",
    "login.subtitle": "Acesse o portal da sua empresa",
    "login.email": "E-mail corporativo",
    "login.password": "Senha",
    "login.submit": "Entrar",
    "login.error": "Credenciais inválidas",
    "login.signing_in": "Entrando...",
    "login.password_show": "Mostrar senha",
    "login.password_hide": "Ocultar senha",
    // ─── Dashboard
    "dash.greeting": "Olá",
    "dash.welcome": "Bem-vindo de volta",
    "dash.pto_balance": "Saldo de PTO",
    "dash.vac_balance": "Saldo de Férias",
    "dash.days": "dias",
    "dash.my_requests": "Minhas solicitações",
    "dash.pending_approvals": "Aguardando minha aprovação",
    "dash.quick_actions": "Ações rápidas",
    "dash.request_pto": "Solicitar PTO/Férias",
    "dash.submit_reimb": "Lançar reembolso",
    "dash.view_docs": "Ver documentos",
    "dash.view_org": "Ver organograma",
    "dash.smart_alerts": "Alertas inteligentes",
    "dash.our_values": "Nossos valores",
    "dash.welcome_chip": "Welcome home",
    "dash.more_alerts": "+ {n} mais · clique no sino no topo",
    // ─── Drafts banner (Sprint 28.3)
    "drafts.banner_title_one": "Você tem 1 rascunho pendente de correção",
    "drafts.banner_title_many": "Você tem {n} rascunhos pendentes de correção",
    "drafts.banner_help": "clique para editar e ressubmeter.",
    "drafts.count_reimb_one": "1 reembolso",
    "drafts.count_reimb_many": "{n} reembolsos",
    "drafts.count_travel_one": "1 viagem",
    "drafts.count_travel_many": "{n} viagens",
    "drafts.cta_reimb": "Reembolsos",
    "drafts.cta_travel": "Viagens",
    "drafts.notif_reimb_title": "Reembolso devolvido como rascunho",
    "drafts.notif_travel_title": "Viagem devolvida como rascunho",
    "drafts.summary_one": "1 reembolso devolvido como rascunho. Clique em um item da lista abaixo (badge \u201cRascunho\u201d) para editar e ressubmeter.",
    "drafts.summary_many": "{n} reembolsos devolvidos como rascunho. Clique em um item da lista abaixo (badge \u201cRascunho\u201d) para editar e ressubmeter.",
    // ─── PTO
    "pto.title": "Férias & PTO",
    "pto.new": "Nova solicitação",
    "pto.type": "Tipo",
    "pto.start": "Data início",
    "pto.end": "Data fim",
    "pto.days": "Dias",
    "pto.reason": "Motivo / observação",
    "pto.submit": "Enviar solicitação",
    "pto.history": "Histórico",
    "pto.empty": "Você ainda não tem solicitações.",
    "pto.balance_pto": "Saldo PTO",
    "pto.balance_vac": "Saldo Férias",
    "pto.expires_in": "Expira em",
    "pto.expired": "EXPIRADO",
    "pto.expires_soon": "Expira em breve",
    "pto.my_rule": "Sua política aplicável",
    "pto.rule_annual": "Concessão anual",
    "pto.rule_carryover": "Carry-over máx.",
    "pto.rule_accrual": "Acúmulo",
    "pto.rule_probation": "Período probatório",
    "pto.insufficient": "Saldo insuficiente para este pedido",
    // ─── Admin
    "admin.pto_mgmt": "Gestão PTO/Férias",
    "admin.pto_rules": "Regras por Localidade",
    "admin.pto_overview_title": "Saldo da equipe",
    "admin.adjust_balance": "Ajustar saldo",
    "admin.adjust_reason": "Motivo do ajuste",
    "admin.adjust_save": "Aplicar ajuste",
    "admin.new_rule": "Nova regra",
    "admin.edit_rule": "Editar regra",
    "admin.delete": "Excluir",
    "admin.used_year": "Usado no ano",
    "admin.expiring_soon_filter": "Expirando em ≤ 60 dias",
    "admin.title": "Administração",
    "admin.users": "Integrantes",
    "admin.depts": "Departamentos",
    "admin.new_dept": "Novo departamento",
    "admin.edit_dept": "Editar departamento",
    "admin.parent_dept": "Departamento pai",
    "admin.projects": "Projetos",
    "admin.workflows": "Fluxos de Aprovação",
    "admin.integrations": "Integrações",
    // ─── Reimbursements
    "reimb.title": "Reembolsos",
    "reimb.tab_overhead": "Overhead",
    "reimb.tab_project": "Custos de projeto",
    "reimb.tab_approvals": "Aprovações",
    "reimb.expense_type": "Tipo de despesa",
    "reimb.amount": "Valor",
    "reimb.currency": "Moeda",
    "reimb.fx_converted": "Convertido (taxa do dia)",
    "reimb.date": "Data",
    "reimb.description": "Descrição",
    "reimb.store": "Loja / fornecedor",
    "reimb.supplier": "Fornecedor",
    "reimb.receipt": "Comprovante (foto ou PDF)",
    "reimb.submit": "Enviar para aprovação",
    "reimb.project": "Projeto",
    "reimb.cost_code": "Código de custo",
    "reimb.item_type": "Tipo de item",
    "reimb.material": "Material",
    "reimb.service": "Serviço",
    "reimb.add_item": "Adicionar item",
    "reimb.history": "Histórico",
    "reimb.invoice": "Invoice",
    "reimb.tip_personal": "💡 Para reembolso de despesas pessoais (refeições, hospedagem, milhagem etc.), use a página Despesas do integrante com a política Sagewood aplicada automaticamente.",
    "reimb.entries": "lançamentos",
    "reimb.col_category": "Categoria",
    "reimb.col_description": "Descrição",
    "reimb.col_cost_code_supplier": "Cost code / Fornecedor",
    "reimb.col_date": "Data",
    "reimb.col_amount": "Valor",
    "reimb.col_receipt": "Recibo",
    "reimb.col_status": "Status",
    "reimb.view_receipt": "Ver",
    "reimb.no_history": "—",
    "reimb.tap_upload": "Clique para anexar foto ou PDF",
    "reimb.upload_help": "JPG, PNG, PDF, HEIC até 10MB",
    "reimb.item_n": "Item {n}",
    "reimb.draft_returned_title": "Este reembolso foi devolvido como rascunho",
    "reimb.draft_returned_help": "Ajuste os dados e ressubmeta para uma nova aprovação.",
    "reimb.manager_comment": "Comentário do gestor",
    "reimb.edit_draft": "Editar rascunho",
    "reimb.editing_draft": "Editando rascunho",
    "reimb.resubmit": "Ressubmeter para aprovação",
    "reimb.save_for_later": "Salvar como rascunho",
    "reimb.save_for_later_hint": "Salve o que já preencheu. Você pode voltar a editar antes de enviar.",
    "reimb.resubmitting": "Enviando...",
    "reimb.project_locked_warning": "Centro de custo / fornecedor não podem ser editados aqui — se precisar trocar, exclua este rascunho e crie um novo reembolso de projeto. Atual:",
    "reimb.invoice_label": "Nº da invoice",
    // ─── Batch decide
    "batch.title": "Aprovação em lote",
    "batch.help": "Decida cada item individualmente. Itens reprovados geram um novo rascunho automaticamente para o integrante corrigir e ressubmeter.",
    "batch.approve_all": "Aprovar todos",
    "batch.reject_all": "Reprovar todos",
    "batch.clear": "Limpar",
    "batch.col_user": "Integrante",
    "batch.col_description": "Descrição",
    "batch.col_date": "Data",
    "batch.col_amount": "Valor",
    "batch.col_decision": "Decisão",
    "batch.approve": "Aprovar",
    "batch.reject": "Reprovar",
    "batch.comment_label": "Comentário (opcional, aplicado a todos os itens deste lote)",
    "batch.comment_placeholder": "ex: Recibos incompletos — favor anexar nota fiscal",
    "batch.empty_help": "Selecione decisões para habilitar o envio.",
    "batch.selected_summary": "{a} aprovar · {r} reprovar · Total selecionado: ${total}",
    "batch.submit": "Enviar decisões ({n})",
    "batch.sending": "Enviando...",
    "batch.processed_title": "Lote processado",
    "batch.processed_approved": "Aprovados",
    "batch.processed_rejected": "Reprovados",
    "batch.processed_drafts": "Rascunhos criados",
    "batch.processed_errors": "Erros",
    "batch.empty_state": "Nenhum reembolso pendente para aprovação no momento.",
    // ─── Travel
    "travel.title": "Viagens",
    "travel.new": "Nova viagem",
    "travel.purpose": "Objetivo",
    "travel.destination": "Destino",
    "travel.departure": "Partida",
    "travel.return": "Retorno",
    "travel.notes": "Notas",
    "travel.notes_optional": "Notas (opcional)",
    "travel.items": "Itens",
    "travel.items_count": "Itens ({n})",
    "travel.category": "Cat.",
    "travel.date": "Data",
    "travel.description": "Descrição",
    "travel.description_opt": "Descrição (opcional)",
    "travel.amount": "Valor ($)",
    "travel.miles": "Milhas",
    "travel.nights": "Noites",
    "travel.cost_center": "Centro de custo",
    "travel.fx_loading": "Calculando câmbio…",
    "travel.fx_rate_label": "taxa",
    "travel.select_cost_center": "Selecione…",
    "travel.new_total": "Novo total: ${total}",
    "travel.fx_locked": "FX travado",
    "travel.original": "Original",
    "travel.request_currency": "Moeda (aplica a todos os itens)",
    "travel.request_currency_help": "Todos os itens desta solicitação serão registrados nesta moeda. O equivalente em USD é travado na aprovação.",
    "travel.confirm_delete": "Excluir este rascunho?",
    "travel.confirm_admin_delete": "Isso moverá a solicitação para a lixeira de deletados. Somente admins podem restaurar. Continuar?",
    "travel.delete_draft_hint": "Excluir este rascunho",
    "travel.admin_delete_hint": "Admin: excluir esta solicitação (histórico preservado para auditoria)",
    "travel.admin_delete_btn": "Excluir (admin)",
    "travel.show_deleted": "Mostrar lixeira (admin)",
    "travel.restore": "Restaurar",
    "travel.deleted_by": "Excluído por",
    "travel.qbo_update_btn": "Atualizar no QBO",
    "travel.qbo_update_confirm": "Isso vai excluir a Bill atual no QBO e recriar com os dados atualizados (descrições, totais, FX). Continuar?",
    "travel.qbo_update_ok": "Bill do QBO atualizada com os dados mais recentes.",
    "travel.qbo_update_hint": "Envia ajustes locais (descrições/FX) para o QBO sem reabrir o fluxo de aprovação",
    "travel.total": "Total: ${total}",
    "travel.policy_warning": "Acima do limite da política",
    "travel.originated_banner": "Este rascunho contém itens reprovados de uma viagem anterior. Ajuste e ressubmeta para nova aprovação.",
    "travel.originated_count_hint": "{n} item(ns) precisa(m) de ajuste — veja o motivo em cada um abaixo.",
    "travel.previous_rejection": "Motivo da reprovação anterior",
    "travel.spawned_card_badge": "Reprovado · Ajustar",
    "travel.at_least_one_item": "Pelo menos 1 item é obrigatório.",
    "travel.alert_set_decision_all": "Defina uma decisão para todos os {total} itens ({missing} pendentes).",
    "travel.alert_draft_min": "Cada item precisa de data e valor para salvar como rascunho.",
    "travel.alert_resubmit_min": "Cada item precisa de data, valor e centro de custo.",
    "travel.alert_missing_receipts": "Anexo de recibo é obrigatório em todos os itens ({n} faltando). Use \"Salvar como rascunho\" se quiser anexar depois.",
    "travel.alert_fill_trip_fields": "Preencha objetivo, destino, partida e retorno.",
    "travel.alert_save_draft_err": "Erro ao salvar rascunho",
    "travel.alert_resubmit_err": "Erro ao ressubmeter",
    "travel.items_with_cost_center": "Itens de despesa (com centro de custo por linha)",
    "travel.view_receipt": "Ver recibo",
    "travel.empty_title": "Nenhuma despesa registrada ainda.",
    "travel.empty_hint": "Comece criando sua primeira despesa de viagem.",
    "travel.create_expense": "Criar despesa",
    "travel.draft_owner_banner_idle": "Este é um rascunho seu. Você pode editar os itens e ressubmeter para aprovação.",
    "travel.draft_owner_banner_editing": "Você está editando este rascunho. Faça os ajustes necessários e clique em",
    "travel.edit_draft": "Editar rascunho",
    "travel.cancel_edit": "Cancelar edição",
    "travel.save_for_later": "Salvar como rascunho",
    "travel.save_for_later_hint": "Salvar o que já preencheu. Você pode editar e enviar depois.",
    "travel.submit_for_approval": "Enviar para aprovação",
    "travel.page_title": "Despesas do integrante",
    "travel.new_expense_title": "Nova despesa de viagem",
    "travel.description_placeholder": "Descrição",
    "travel.sync_error": "Erro de sincronização",
    "travel.qbo_retry_sync": "Tentar sincronizar de novo",
    "travel.qbo_resync_running": "Sincronizando…",
    "travel.qbo_resync_ok": "Sincronização com QBO refeita com sucesso.",
    "travel.qbo_resync_bg": "A sincronização continua em segundo plano. Atualize a página em instantes.",
    "travel.employee_vendor": "Fornecedor (integrante)",
    "travel.synced_at": "Sincronizado em",
    "travel.approval_chain": "Cadeia de aprovação",
    "travel.waiting_for": "Aguardando",
    "common.cost_code": "Código de custo",
    "common.approval_trail": "Histórico de aprovação",
    "common.last_payment_check": "Última verificação de pagamento",
    "common.qbo_not_configured": "QBO não configurado",
    "common.sync_error": "Erro de sincronização",
    "common.search_placeholder": "Buscar…",
    "common.no_results": "Nenhum resultado",
    "common.write_comment": "Escreva um comentário…",
    "common.cancel": "Cancelar",
    "common.save": "Salvar",
    "common.currency": "Moeda",
    "common.delete": "Excluir",
    "common.deleted": "Excluído",
    "common.edit": "Editar",
    "common.export_csv": "Exportar CSV",
    "common.actions": "Ações",
    "common.status": "Status",
    "common.date": "Data",
    "common.amount": "Valor",
    "common.description": "Descrição",
    "common.add": "Adicionar",
    "common.loading": "Carregando…",
    "admin.pto.search": "Buscar nome ou e-mail…",
    "admin.pto.all_locations": "Todas as localidades",
    "admin.pto.all_contracts": "Todos os contratos",
    "admin.pto.balance": "Saldo de PTO",
    "admin.pto.vacation_balance": "Saldo de Férias",
    "admin.pto.expires_at": "PTO expira em",
    "admin.pto.vacation_expires_at": "Férias expiram em",
    "admin.pto.total_outstanding": "Total de PTO em aberto",
    "admin.pto.total_vacation_outstanding": "Total de Férias em aberto",
    "admin.pto.pto_used_year": "PTO usado este ano",
    "admin.pto.vac_used_year": "Férias usadas este ano",
    "admin.pto.annual_days": "Dias anuais de PTO",
    "admin.pto.vacation_annual_days": "Dias anuais de Férias",
    "admin.pto.accrual_method": "Método de acúmulo",
    "admin.pto.probation_months": "Meses de período de experiência",
    "admin.pto.pto_expiration_months": "Expiração PTO (meses)",
    "admin.pto.vac_expiration_months": "Expiração Férias (meses)",
    "admin.pto.monthly_accrual": "Acúmulo mensal",
    "admin.pto.annual_lump": "Concessão anual",
    "admin.pto.pto_annual_label": "PTO anual",
    "admin.pto.vac_annual_label": "Férias anuais",
    "admin.pto.pto_carryover": "Carry-over PTO",
    "admin.pto.vac_carryover": "Carry-over Férias",
    "admin.costcode.search": "Buscar código ou descrição…",
    "admin.costcode.code": "Código",
    "admin.supplier.search": "Buscar fornecedor…",
    "admin.supplier.all_types": "Todos os tipos",
    "admin.new_employee": "Novo integrante",
    "admin.job_title": "Cargo",
    "admin.contract_type": "Tipo de contrato",
    "admin.access_profile": "Perfil de acesso",
    "admin.default_payment": "Componentes de pagamento padrão",
    "admin.payment.amount": "Valor",
    "admin.payment.label": "Rótulo",
    "admin.payment.per_hour": "Por hora",
    "admin.payment.per_day": "Por dia",
    "admin.payment.per_month": "Por mês",
    "admin.payment.no_approvers": "Sem aprovadores",
    "admin.payment.default_payment": "Pagamento padrão",
    "admin.payment.config_title": "Configuração de pagamento",
    "admin.payment.quick_presets": "Modelos rápidos",
    "admin.menu_item": "Item do menu",
    "admin.reim_counter": "Contador de reembolsos",
    "admin.corp_notif_rules": "Regras de notificação corporativa",
    "admin.reload_demo": "Recarregar dados de demonstração",
    "qbo.expand_all": "Expandir tudo",
    "qbo.collapse_all": "Recolher",
    "qbo.vendor_id": "QBO Vendor Id",
    "qbo.filter_by_email": "Filtrar por nome/e-mail…",
    "offb.awaiting_approval": "Aguardando aprovação",
    "offb.exit_interview": "Entrevista de desligamento",
    "offb.frankcrum_unenroll": "Cancelamento FrankCrum",
    "offb.docusign_term": "Rescisão DocuSign",

    // ─── Sidebar / nav (extras)
    "nav.trainings": "Treinamentos",
    "nav.onboarding": "Integração",
    "nav.departments": "Hub de departamentos",
    "nav.salary_matrix": "Matriz salarial",
    "nav.branding": "Marca",
    "nav.superadmin": "Super Admin · Empresas",
    "nav.service_contracts": "Prestação de Serviços",
    "ext.provider_badge": "Prestador · {name}",
    "sc.title": "Prestação de Serviços",
    "sc.subtitle": "Contratos entre empresas e alocação de prestadores",
    "sc.new_contract": "Novo contrato",
    "sc.create_btn": "Criar contrato",
    "sc.no_contracts": "Nenhum contrato de prestação de serviços ainda. Crie o primeiro para conectar duas empresas.",
    "sc.provider": "Prestadora",
    "sc.client": "Cliente",
    "sc.my_role": "Papel da minha empresa",
    "sc.partner_company": "Empresa parceira",
    "sc.contract_name": "Nome do contrato",
    "sc.code": "Código",
    "sc.description": "Descrição",
    "sc.start": "Início",
    "sc.end": "Fim",
    "sc.status_active": "Ativo",
    "sc.status_ended": "Encerrado",
    "sc.status_inactive": "Inativo",
    "sc.allocations": "Pessoas alocadas",
    "sc.allocated_n": "{n} alocado(s)",
    "sc.add_people": "Alocar pessoa da minha empresa",
    "sc.allocate": "Alocar",
    "sc.select_employee": "Selecionar colaborador…",
    "sc.no_allocations": "Ninguém alocado neste contrato ainda.",
    "sc.col_person": "Pessoa (prestadora)",
    "sc.designated_manager": "Gestor designado (cliente)",
    "sc.no_manager": "Sem gestor designado",
    "sc.remove": "Remover",
    "sc.removed": "Alocação removida",
    "sc.deactivate": "Desativar alocação",
    "sc.activate": "Reativar alocação",
    "sc.end_contract": "Encerrar contrato",
    "sc.reactivate_contract": "Reativar contrato",
    "sc.provider_hint": "Sua empresa é a PRESTADORA: você seleciona quem está alocado neste contrato.",
    "sc.client_hint": "Sua empresa é a CLIENTE: você designa o gestor responsável por cada pessoa alocada.",
    "sc.transfer_title": "Transferir colaborador para outra empresa",
    "sc.transfer_desc": "Move o colaborador e todo o histórico dele (férias, reembolsos, avaliações…) para a empresa destino. Opcionalmente, já cria a alocação dele no contrato de prestação de serviços apontando para o gestor atual.",
    "sc.transfer_btn": "Transferir colaborador",
    "sc.transfer_user": "Colaborador",
    "sc.transfer_target": "Empresa destino",
    "sc.transfer_contract": "Alocar no contrato (opcional)",
    "sc.transfer_manager": "Gestor designado (opcional — padrão: gestor atual)",
    "sc.transfer_confirm": "Confirmar transferência",
    "sc.transfer_warning": "Atenção: o colaborador passará a acessar o portal pela empresa destino e precisará entrar novamente.",
    "sc.transfer_done": "Transferência concluída",
    "sc.access_title": "Acesso multiempresa",
    "sc.access_desc": "Conceda a admins/RH de outras empresas acesso a ESTA empresa. Eles verão um seletor de empresa no menu.",
    "sc.access_grant": "Conceder acesso",
    "sc.access_email": "E-mail do usuário (admin/RH de outra empresa)",
    "sc.access_revoke": "Revogar",
    "sc.access_none": "Ninguém de fora tem acesso a esta empresa.",
    "sc.created": "Contrato criado",
    "sc.saved": "Salvo",
    "sc.error": "Erro ao salvar",
    "sc.fill_required": "Preencha os campos obrigatórios",
    "sc.costs": "Custos e taxa",
    "sc.cost_item": "Tipo de custo — despesas (conta QBO)",
    "sc.cost_center": "Centro de custo — despesas",
    "sc.cost_item_short": "Tipo de custo",
    "sc.cost_center_short": "Centro de custo",
    "sc.cost_item_free": "Tipo de custo (texto livre — QBO não sincronizado)",
    "sc.fee_rate_type": "Alocação do recurso (taxa)",
    "sc.rate_none": "Sem taxa",
    "sc.rate_hourly": "Por hora",
    "sc.rate_monthly": "Por mês",
    "sc.rate_month_short": "mês",
    "sc.rate_value": "Valor (USD)",
    "sc.fee_cost_item": "Tipo de custo do recurso (opcional)",
    "sc.fee_cost_center": "Centro de custo do recurso (opcional)",
    "sc.fee_same_hint": "Se vazio, usa o tipo/centro de custo das despesas.",
    "sc.saved_btn": "Salvar custos",
    "sc.billing_title": "Faturamento",
    "sc.billing_subtitle": "Despesas aprovadas no mês + custo do recurso — pronto para faturar a empresa cliente",
    "sc.billing_person": "Pessoa",
    "sc.billing_rate": "Taxa",
    "sc.billing_hours": "Horas",
    "sc.billing_fee": "Recurso",
    "sc.billing_reimb": "Reembolsos",
    "sc.billing_travel": "Viagens",
    "sc.billing_total": "Total",
    "sc.billing_grand": "Total do contrato",
    "sc.billing_empty": "Sem pessoas alocadas ou valores neste mês.",
    "sc.billing_criteria": "Critério: despesas com decisão de aprovação dentro do mês selecionado + taxa do recurso (mensal ou horas × valor/hora).",
    "approvals.billing": "Faturamento",
    "sc.appr_col": "Aprovação",
    "sc.appr_not_submitted": "Não enviado",
    "sc.appr_pending": "Aguardando gestor",
    "sc.appr_approved": "Aprovado",
    "sc.appr_rejected": "Rejeitado",
    "sc.appr_stale": "Valores alterados",
    "sc.appr_submit": "Enviar para aprovação",
    "sc.appr_submitted_n": "{n} item(ns) enviado(s) para aprovação do gestor",
    "sc.appr_requested": "Solicitado",
    "sc.appr_in_review": "Em aprovação",
    "nav.tagline": "Portal de pessoas",
    "nav.logout_label": "sair",

    // ─── Common (extras)
    "common.former": "Antigo",
    "common.uploading": "Enviando...",
    "common.update": "Atualizar",
    "common.new": "Novo",
    "common.error_generic": "Ocorreu um erro",
    "common.export_pdf": "Exportar PDF",
    "common.download_pdf": "Baixar PDF detalhado",
    "common.zoom_in": "Aumentar zoom",
    "common.zoom_out": "Diminuir zoom",
    "common.expand": "Expandir",
    "common.collapse": "Recolher",
    "common.dismiss": "Dispensar",
    "common.dismiss_all": "Limpar todos",
    "common.required_mark": "Obrigatório",
    "common.role": "Função",
    "common.email": "E-mail",
    "common.name": "Nome",
    "common.phone": "Telefone",
    "common.department": "Departamento",
    "common.location": "Localização",
    "common.manager": "Gestor",
    "common.hire_date": "Data de admissão",
    "common.birthday": "Aniversário",
    "common.bio": "Bio",
    "common.job_title": "Cargo",
    "common.contract": "Contrato",
    "common.password": "Senha",
    "common.work_email": "E-mail corporativo",
    "common.personal_email": "E-mail pessoal",
    "common.emergency_contact": "Contato de emergência",
    "common.value": "Valor",
    "common.category": "Categoria",
    "common.title": "Título",
    "common.notes": "Notas",
    "common.tags": "Tags",
    "common.active": "Ativo",
    "common.inactive": "inativo",
    "common.history": "Histórico",
    "common.comment": "Comentário",

    // ─── Values (Sagewood)
    "values.quality": "Qualidade",
    "values.care": "Cuidado",
    "values.commitment": "Compromisso",

    // ─── Dashboard (extras)
    "dash.dismiss_all": "Limpar todos",
    "dash.dismiss_alert": "Dispensar",
    "dash.hint_pto": "PTO",
    "dash.hint_vac": "Férias",
    "dash.hint_pending": "Pendentes",
    "dash.hint_inbox": "Caixa de entrada",

    // ─── Profile (extras)
    "profile.edit": "Editar perfil",
    "profile.change_password": "Alterar senha",
    "profile.payment_config": "Configuração de pagamento",
    "profile.cost_code": "Código de custo",
    "profile.pto_balance": "Saldo de PTO",
    "profile.vacation_balance": "Saldo de férias",
    "profile.password_mismatch": "As senhas não coincidem",
    "profile.password_updated": "Senha atualizada.",
    "profile.current_password": "Senha atual *",
    "profile.new_password": "Nova senha (≥6 caracteres) *",
    "profile.confirm_password": "Confirme a nova senha *",
    "profile.days_unit": "dias",
    "profile.update_btn": "Atualizar",

    // ─── EmployeeProfileModal
    "profile_modal.salary_history": "Histórico salarial",
    "profile_modal.no_salary_movement": "Sem movimentações salariais registradas.",
    "profile_modal.out_of_band": "exceção fora da política",
    "profile_modal.by": "por",
    "profile_modal.former": "Antigo",
    "profile_modal.loading": "Carregando…",

    // ─── Directory (extras)
    "dir.birthdays_title": "Aniversariantes",
    "dir.person_one": "pessoa",
    "dir.person_many": "pessoas",
    "dir.no_birthdays": "Nenhum aniversário cadastrado para este mês.",
    "dir.today_marker": "· HOJE 🎉",
    "dir.results": "resultados",
    "dir.active_tab": "Integrantes ativos",
    "dir.former_tab": "Antigos integrantes",
    "dir.former_badge": "Antigo",
    "dir.hired_on": "Admitido em",
    "dir.month_1": "Janeiro",
    "dir.month_2": "Fevereiro",
    "dir.month_3": "Março",
    "dir.month_4": "Abril",
    "dir.month_5": "Maio",
    "dir.month_6": "Junho",
    "dir.month_7": "Julho",
    "dir.month_8": "Agosto",
    "dir.month_9": "Setembro",
    "dir.month_10": "Outubro",
    "dir.month_11": "Novembro",
    "dir.month_12": "Dezembro",

    // ─── OrgChart (extras)
    "org.subtitle_people": "integrantes",
    "org.subtitle_depts": "departamentos",
    "org.view_tree": "Árvore",
    "org.view_list": "Lista",
    "org.expand_all": "Expandir tudo",
    "org.collapse_all": "Recolher tudo",
    "org.export_pdf": "Exportar PDF",
    "org.export_failed": "Falha ao exportar PDF",
    "org.search_placeholder": "Buscar por nome, cargo, e-mail...",
    "org.departments_title": "Departamentos",
    "org.head": "Líder",
    "org.no_head": "Sem líder designado",
    "org.colab_short": "colab.",
    "org.report_one": "subordinado",
    "org.report_many": "subordinados",

    // ─── Loans
    "loans.title": "Empréstimos do integrante",
    "loans.count_one": "empréstimo(s)",
    "loans.active": "ativo(s)",
    "loans.register_btn": "Registrar empréstimo",
    "loans.register_title": "Registrar empréstimo para integrante",
    "loans.confidentiality": "Empréstimos são altamente confidenciais. Apenas o RH pode criá-los em nome de um integrante.",
    "loans.employee_required": "Integrante *",
    "loans.select_employee": "Selecione o integrante...",
    "loans.amount_required": "Valor (USD) *",
    "loans.installments_required": "Parcelas (1-24) *",
    "loans.monthly_estimate": "Pagamento mensal estimado:",
    "loans.disbursement_date_required": "Data desejada de desembolso *",
    "loans.reason_required": "Motivo *",
    "loans.notes_label": "Notas",
    "loans.saving": "...",
    "loans.error_select_employee": "Selecione o integrante deste empréstimo.",
    "loans.label_singular": "Empréstimo",
    "loans.agreement": "Contrato de Empréstimo",
    "loans.approved_by": "Aprovado por:",
    "loans.approval_chain": "Cadeia de aprovação",
    "loans.payment_schedule": "Cronograma de pagamentos",
    "loans.paid_label": "Pago:",
    "loans.outstanding_label": "Em aberto:",
    "loans.due": "Vencimento",
    "loans.paid_on": "Pago em",
    "loans.mark_paid": "Marcar pago",
    "loans.your_approval": "Sua aprovação ({role})",
    "loans.comment_optional": "Comentário (opcional)",
    "loans.reject": "Rejeitar",
    "loans.approve": "Aprovar",
    "loans.mine": "Meus",
    "loans.all": "Todos",
    "loans.no_loans": "Sem empréstimos.",
    "loans.repaid_pct": "Reembolsado",
    "loans.status_requested": "solicitado",
    "loans.status_active": "ativo",
    "loans.status_paid": "pago",
    "loans.status_rejected": "rejeitado",

    // ─── Documents (extras)
    "docs.subtitle_count": "documentos",
    "docs.new_btn": "Novo documento",
    "docs.modal_new": "Novo documento",
    "docs.modal_edit": "Editar documento",
    "docs.field_title": "Título *",
    "docs.field_description": "Descrição",
    "docs.field_category": "Categoria *",
    "docs.field_url": "URL do documento ou upload",
    "docs.url_placeholder": "https://... ou faça upload abaixo",
    "docs.upload_failed": "Falha no upload:",
    "docs.delete_confirm": "Excluir \"{title}\"?",

    // ─── Manager Cockpit (extras)
    "cockpit.title": "Cockpit do gestor",
    "cockpit.greeting": "Olá, {name}. Sua visão consolidada de time.",
    "cockpit.scope_direct": "Time direto",
    "cockpit.scope_extended": "Time estendido",
    "cockpit.scope_direct_label": "direto",
    "cockpit.scope_extended_label": "estendido",
    "cockpit.tab_overview": "Visão geral",
    "cockpit.tab_calendar": "Calendário",
    "cockpit.tab_timeline": "Linha do tempo",
    "cockpit.tab_risks": "Riscos",
    "cockpit.my_team": "Meu time",
    "cockpit.need_now": "Precisa de você agora",
    "cockpit.items": "itens",
    "cockpit.all_clear": "Tudo em dia! Nenhum item pendente.",
    "cockpit.people": "Pessoas",
    "cockpit.kpi_headcount": "Efetivo",
    "cockpit.kpi_pto_planned": "PTO planejado",
    "cockpit.kpi_performance": "Performance",
    "cockpit.kpi_reimb_ytd": "Reembolsos YTD",
    "cockpit.kpi_trainings": "Treinamentos",
    "cockpit.kpi_loans": "Empréstimos",
    "cockpit.kpi_scope": "escopo",
    "cockpit.kpi_next_90d": "próx. 90d · saldo total {n}d",
    "cockpit.kpi_evals_90d": "{n} avaliações em 90d",
    "cockpit.kpi_pending_approval": "{n} aguardando aprovação",
    "cockpit.kpi_overdue_team": "{n} vencidos no time",
    "cockpit.kpi_loans_sub": "aguardando sua decisão",
    "cockpit.urgency_high": "Urgente",
    "cockpit.urgency_normal": "Pendente",
    "cockpit.urgency_soft": "Leve",
    "cockpit.col_employee": "Integrante",
    "cockpit.col_dept": "Depto",
    "cockpit.col_pto_balance": "Saldo PTO",
    "cockpit.col_next_vacation": "Próx. férias",
    "cockpit.col_last_1on1": "Última 1:1",
    "cockpit.col_last_eval": "Última avaliação",
    "cockpit.col_trainings": "Treinamentos",
    "cockpit.no_employees": "Sem integrantes no escopo.",
    "cockpit.overdue_short": "vencidos",
    "cockpit.unavailable": "Cockpit indisponível",
    "cockpit.load_error": "Falha ao carregar Cockpit",
    "cockpit.1on1": "1:1",
    "cockpit.next_1on1": "Próxima 1:1",
    "cockpit.notes_label": "Anotações",
    "cockpit.notes_placeholder": "Como foi a conversa, principais pontos discutidos…",
    "cockpit.action_items": "Action items (uma por linha)",
    "cockpit.action_items_placeholder": "• Revisar plano de carreira\n• Inscrever em treinamento X",
    "cockpit.save_1on1": "Salvar 1:1",
    "cockpit.saving_1on1": "Salvando…",
    "cockpit.history": "Histórico",
    "cockpit.next_label": "Próxima:",
    "cockpit.error_saving": "Erro ao salvar",


    "travel.cancel": "Cancelar",
    "travel.resubmit": "Ressubmeter para aprovação",
    "travel.resubmitting": "Enviando...",
    "travel.edit_form_title": "Editar rascunho",
    "travel.your_approval": "Sua aprovação ({role})",
    "travel.comment_optional": "Comentário (opcional)",
    "travel.itemized_toggle": "Decidir item a item",
    "travel.itemized_help_1": "Marque cada linha como",
    "travel.itemized_help_2": "ou",
    "travel.itemized_help_3": "na lista de itens acima. Itens reprovados serão movidos para um novo rascunho do integrante.",
    "travel.decided_count": "Decididos: {n} / {total}",
    "travel.confirm_decisions": "Confirmar {n} decisão(ões)",
    "travel.approve": "Aprovar",
    "travel.reject": "Rejeitar",
    "travel.line_approve": "Aprovar",
    "travel.line_reject": "Reprovar",
    "travel.line_approved": "Linha aprovada",
    "travel.line_rejected": "Linha reprovada",
    "travel.decided_count_drafts": "{n} aprovado(s) · {r} reprovado(s)",
    "travel.spawned_draft": "Rascunho criado para os itens reprovados:",
    "travel.expense_items": "Itens da despesa",
    "travel.mark_paid": "Marcar como pago",
    "travel.filter_all": "todos",
    "travel.filter_draft": "rascunho",
    "travel.filter_pending": "Aguardando aprovação",
    "travel.filter_ready": "Pronto p/ financeiro",
    "travel.filter_submitted_qbo": "Enviado ao QBO",
    "travel.filter_paid": "Pago",
    "travel.filter_rejected": "Rejeitado",
    "travel.compliance_flagged": "{n} item(ns) excede(m) a política",
    "travel.cat_meals": "Refeições",
    "travel.cat_lodging": "Hospedagem",
    "travel.cat_transport": "Transporte",
    "travel.cat_mileage": "Milhagem",
    "travel.cat_other": "Outros",
    // ─── Approvals
    "approvals.title": "Aprovações pendentes",
    "approvals.pto": "PTO / Férias",
    "approvals.reimb": "Reembolsos",
    "approvals.approve": "Aprovar",
    "approvals.reject": "Rejeitar",
    "approvals.comment": "Comentário",
    "approvals.empty": "Nenhuma aprovação pendente.",
    // ─── Documents / directory / org / profile
    "docs.title": "Central de Documentos",
    "docs.search": "Buscar documentos...",
    "directory.title": "Diretório de integrantes",
    "directory.search": "Buscar por nome, cargo, departamento...",
    "directory.contract": "Contrato",
    "directory.location": "Localidade",
    "directory.all": "Todos",
    "org.title": "Organograma",
    "profile.title": "Meu Perfil",
    // ─── Status
    "status.draft": "Rascunho",
    "status.pending": "Pendente",
    "status.pending_approval": "Aguardando aprovação",
    "status.approved": "Aprovado",
    "status.rejected": "Rejeitado",
    "status.submitted_on_redteam": "Submetido no RedTeam",
    "status.submitted_to_qbo": "Submetido ao QBO",
    "status.ready_for_finance": "Pronto para Finance",
    "status.paid": "Pago",
    // ─── RedTeam / QBO sync (in modal)
    "reimb.submit_redteam": "Submeti no RedTeam",
    "reimb.submit_redteam_help": "Informe a referência do envio no RedTeam (opcional).",
    "reimb.submit_redteam_ref": "Referência RedTeam",
    "reimb.submit_redteam_action": "Confirmar submissão",
    "reimb.qbo_sync_title": "Sincronização QuickBooks",
    "reimb.qbo_payment_status": "Status de pagamento",
    "reimb.qbo_unpaid": "Pendente de pagamento",
    "reimb.qbo_paid": "Pago no QBO",
    "reimb.qbo_partial": "Parcialmente pago",
    "reimb.qbo_synced_at": "Sincronizado em",
    "reimb.qbo_vendor_credit": "VendorCredit",
    "reimb.qbo_bill": "Bill (integrante)",
    "reimb.qbo_logs": "Logs QBO",
    "reimb.qbo_retry_sync": "Tentar sincronizar de novo",
    "reimb.qbo_resync_running": "Sincronizando…",
    "reimb.qbo_resync_ok": "Sincronização com QBO refeita com sucesso.",
    "reimb.qbo_resync_bg": "A sincronização continua em segundo plano. Atualize a página em instantes.",
    "reimb.reopen_from_qbo": "Reabrir (voltar p/ aprovado)",
    "reimb.reopen_hint": "Apaga o Bill e o VendorCredit no QBO e volta o reembolso para o status Aprovado, permitindo correção. Só disponível enquanto não estiver pago.",
    "reimb.reopen_confirm": "Isso apagará o Bill e o VendorCredit no QuickBooks e voltará o reembolso para APROVADO. Deseja continuar?",
    "reimb.reopen_ok": "Reembolso reaberto. Os registros QBO foram apagados e o reembolso voltou para Aprovado.",
    "reimb.history_log": "Linha do tempo",
    "reimb.dry_run_warning": "MODO DRY-RUN — chamadas QBO simuladas",
    // ─── QBO Settings (Sprint 28)
    "qbo.conn_title": "Conexão QBO desta empresa",
    "qbo.section_connection": "Configuração da conexão com o QBO",
    "qbo.section_vendors_title": "Vendors (Fornecedores) — fonte oficial",
    "qbo.section_vendors_desc": "Selecione os vendors do QBO que devem ser trazidos para o sistema (source of truth para fornecedores).",
    "qbo.section_coa_title": "Chart of Accounts",
    "qbo.section_coa_desc": "Selecione as contas do QBO disponíveis. O de‑para com tipos de despesa é feito na tela de Travel Policy.",
    "qbo.section_classes_title": "Cost Codes (QBO Classes) — fonte oficial",
    "qbo.section_classes_desc": "Selecione as classes do QBO que serão usadas como código de custo no sistema.",
    "qbo.section_departments_title": "Departamentos",
    "qbo.section_departments_desc": "Vincule cada departamento à QBO Class correspondente (o departamento vira a fonte de verdade).",
    "qbo.qbo_class": "QBO Class",
    "qbo.chart_of_accounts": "Chart of Accounts",
    "qbo.sync_classes_first": "Sincronize as Classes do QBO antes de mapear os departamentos.",
    "qbo.no_departments": "Nenhum departamento cadastrado ainda.",
    "qbo.synced_ok": "Sincronizado com QBO",
    "qbo.new_dept": "Novo departamento",
    "qbo.edit_dept": "Editar departamento",
    "qbo.parent_dept": "Departamento pai",
    "qbo.dept_delete_confirm": "Excluir departamento \"{name}\"?",
    "qbo.coa_travel_hint": "Após sincronizar, vá em Travel Policy → Item para escolher a conta específica de cada tipo de despesa.",
    "qbo.conn_desc": "Cada empresa pode usar sua própria conta QuickBooks. As credenciais ficam isoladas por tenant.",
    "qbo.conn_connected": "Conectado",
    "qbo.conn_disconnected": "Não conectado",
    "qbo.tenant": "Tenant",
    "qbo.realm": "Realm",
    "qbo.environment": "Ambiente",
    "qbo.dry_run": "Simulação (dry-run)",
    "qbo.yes": "Sim",
    "qbo.no": "Não",
    "qbo.btn_update": "Atualizar credenciais",
    "qbo.btn_connect": "Conectar QBO",
    "qbo.btn_disconnect": "Desconectar",
    "qbo.client_id": "QBO Client ID",
    "qbo.client_secret": "QBO Client Secret",
    "qbo.realm_id": "Realm ID (Company ID)",
    "qbo.environment_label": "Ambiente",
    "qbo.env_production": "Produção",
    "qbo.env_sandbox": "Sandbox",
    "qbo.refresh_token": "Refresh Token",
    "qbo.refresh_help": "Obtenha estes valores no Intuit Developer Portal > Keys & OAuth. O Refresh Token é gerado após autorizar a app no QBO.",
    "qbo.mode": "Modo",
    "qbo.btn_test": "Testar conexão",
    "qbo.testing": "Testando…",
    "qbo.test_ok": "Conectado a",
    "qbo.broker_url_label": "URL de token (broker assinado)",
    "qbo.broker_url_help": "Cole a URL assinada que retorna o access_token do QBO (ex: Azure Function). O código embutido na URL é a credencial — não é exibido de volta.",
    "qbo.show_advanced": "Opções avançadas (OAuth direto)",
    "qbo.hide_advanced": "Ocultar avançado",
    "qbo.advanced_hint": "Modo legado: use apenas se você não tem broker URL. Client ID, Secret, Realm e Refresh Token do Intuit Developer Portal.",
    "qbo.bulk_retry": "Tentar sincronizar pendentes",
    "qbo.bulk_retrying": "Sincronizando…",
    "qbo.bulk_retry_confirm": "Isso vai tentar re-sincronizar TODOS os travels e reembolsos com erro. Confirmar?",
    "qbo.bulk_retry_done": "{synced} sincronizados · {errors} com erro · {skipped} ignorados (de {total} tentados)",
    "qbo.btn_cancel": "Cancelar",
    "qbo.btn_save": "Salvar conexão",
    "qbo.saving": "Salvando...",
    "qbo.save_success": "Conexão QBO salva. As próximas sincronizações usarão estas credenciais.",
    "qbo.disconnect_confirm": "Desconectar QBO desta empresa? Os tokens e configurações serão removidos. Os recibos sincronizados anteriormente permanecem.",
    "qbo.disconnected": "Conexão QBO removida.",
    "qbo.mapping_title": "Sincronização e mapeamento QBO",
    "qbo.mapping_desc": "Catálogo do QuickBooks Online + de-para para reembolsos de viagem.",
    "qbo.sync": "Sincronizar",
    "qbo.card_vendors": "Fornecedores (QBO)",
    "qbo.card_accounts": "Contas (Plano de Contas)",
    "qbo.card_classes": "Classes (Projetos/CC)",
    // ─── Header & misc
    "header.tour_guided": "Tour guiado",
    "header.alerts": "Alertas",
    // ─── Common (extras post-iter35)
    "common.required": "Campo obrigatório",
    "common.refresh": "Atualizar",
    "common.search": "Buscar",
    "common.back": "Voltar",
    "common.close": "Fechar",
    "common.confirm": "Confirmar",
    "common.optional": "opcional",
    "common.yes": "Sim",
    "common.no": "Não",
    "common.error": "Erro",
    "common.success": "Sucesso",
    "common.select": "Selecione…",
    "common.all": "Todos",
    "common.none": "—",
    "common.total": "Total",
    // ─── i18n audit additions (Jun 2026)
    "common.employee": "Integrante",
    "common.admin": "Admin",
    "common.type": "Tipo",
    "common.approve": "Aprovar",
    "common.reject": "Rejeitar",
    "common.submit": "Enviar",
    "common.next": "Próximo",
    "common.view": "Ver",
    "common.open": "Abrir",
    "common.done": "Concluído",
    "common.clear": "Limpar",
    "common.hours": "Horas",
    "common.period": "Período",
    "common.company": "Empresa",
    "common.total_hours": "Total de horas",
    "common.nothing_export": "Nada para exportar.",
    "common.upload_failed": "Falha no upload:",
    "common.download_failed": "Falha no download:",
    "common.delete_named": "Excluir \"{title}\"?",
    "common.delete_q": "Excluir?",
    "status.in_progress": "Em andamento",
    "status.completed": "Concluído",
    "status.active": "Ativo",
    "status.closed": "Encerrado",
    "status.failed": "Reprovado",
    "status.open": "Aberto",
    "travel.scope_mine": "Minhas",
    "travel.scope_all": "Todas",
    "travel.requests_policy": "{n} solicitação(ões) · Política:",
    "travel.flagged_n": "{n} fora da política",
    "travel.exceed_policy": "item(ns) excedem a política",
    "travel.policy_alerts": "Alertas da política",
    "travel.reopen_from_qbo": "Reabrir (voltar p/ rascunho)",
    "travel.reopen_hint": "Apaga o Bill no QBO e volta a viagem para rascunho para correção. Só disponível enquanto o Bill não estiver pago.",
    "travel.reopen_confirm": "Isso apagará o Bill no QuickBooks e voltará a viagem para RASCUNHO. Deseja continuar?",
    "travel.reopen_ok": "Viagem reaberta. O QBO Bill foi apagado e o registro está em rascunho.",
    "travel.qbo_synced": "sincronizado",
    "travel.bill_employee": "Bill (integrante)",
    "travel.per_day": "por dia",
    "travel.per_night": "por noite",
    "travel.per_mile": "por milha",
    "admin.tab_payroll": "Empresas de folha",
    "admin.tab_cost_codes": "Códigos de custo",
    "admin.tab_suppliers": "Fornecedores",
    "admin.tab_job_profiles": "Perfis de cargo",
    "admin.tab_access_profiles": "Perfis de acesso",
    "admin.tab_workflows": "Fluxos de aprovação",
    "admin.tab_travel_policy": "Política de viagens",
    "admin.tab_demo": "Demo & Manual",
    "admin.tab_notifications": "Regras de notificação",
    "admin.tab_menu_visibility": "Visibilidade do menu",
    "admin.tab_deploy_check": "Pré-Deploy Check",
    "admin.tab_permissions": "Permissões",
    "deploycheck.title": "Checagem pré-deploy",
    "deploycheck.desc": "Roda a suíte de regressão completa (segurança + smoke de todos os módulos) contra este Preview. Só faça Deploy com veredito GO.",
    "deploycheck.run_btn": "Rodar checagem",
    "deploycheck.running": "Rodando…",
    "deploycheck.running_hint": "Executando a suíte de testes (~30-60s). O resultado aparece aqui automaticamente.",
    "deploycheck.started": "Checagem iniciada",
    "deploycheck.go": "GO — seguro para Deploy",
    "deploycheck.nogo": "NO-GO — não faça deploy",
    "deploycheck.passed": "Passaram",
    "deploycheck.failed": "Falharam",
    "deploycheck.total": "Total",
    "deploycheck.skipped": "Pulados",
    "deploycheck.failures": "Testes com falha",
    "deploycheck.preview_only": "Disponível apenas no ambiente Preview (não roda em produção).",
    "deploycheck.last_run": "Última execução",
    "deploycheck.by": "por",
    "deploycheck.never_run": "Nenhuma checagem executada ainda.",
    "deploycheck.output": "Ver saída completa",
    "admin.tab_qbo_admin": "QBO Sync & Configuração",
    "admin.qbo_admin_desc": "Configure conexão, catálogos QBO, departamentos, códigos de custo e fornecedores em um só lugar.",
    "admin.qbo_section_departments": "Departamentos",
    "admin.qbo_section_cost_codes": "Códigos de custo",
    "admin.qbo_section_suppliers": "Fornecedores",
    "admin.qbo_section_qbo": "QBO — Conexão e catálogos",
    "admin.payroll_company": "Payroll Company",
    "admin.payroll_company_ph": "ex.: Sagewood Vendor, FrankCrum, Lummar",
    "admin.applicable_rule": "Regra aplicável",
    "admin.payroll_company_hint": "Deixe em branco para aplicar a todas as payroll companies",
    "admin.no_payroll_companies": "Cadastre payroll companies em Regras por Localidade primeiro",
    "admin.column_menu_item": "Item do menu",
    "admin.column_hide_for": "Ocultar p/ {role}",
    "admin.column_shortcuts": "Atalhos",
    "admin.saved_at": "Salvo às {time}",
    "admin.save_config": "Salvar configuração",
    "admin.saving": "Salvando…",
    "admin.role_employee": "Integrante",
    "admin.role_manager": "Gestor",
    "admin.role_ceo": "CEO",
    "admin.role_hr": "RH",
    "admin.employees_name": "Nome",
    "admin.employees_active": "Ativo",
    "admin.employees_inactive": "Inativo",
    "admin.actions_edit": "Editar",
    "admin.list_view": "Lista",
    "admin.filter_active": "Ativos",
    "admin.filter_inactive": "Inativos",
    "admin.new_collab": "Novo integrante",
    "admin.of_x_collabs": "{filtered} de {total} integrantes",
    "admin.expand_all": "Expandir tudo",
    "admin.collapse_all": "Colapsar tudo",
    "admin.direct_reports": "diretos",
    "admin.total_reports": "total",
    "admin.registration_data": "Dados de cadastro",
    "admin.contact_section": "Contato",
    "admin.dates_balances": "Datas & saldos",
    "admin.close_pwd": "Fechar",
    "admin.change_pwd": "Trocar senha",
    "admin.save_pwd": "Salvar senha",
    "admin.save_changes": "Salvar alterações",
    "admin.saving_pwd": "Salvando…",
    "admin.emergency_contact_ph": "Nome + telefone",
    "admin.save_fail": "Falha ao salvar.",
    "admin.reset_pwd_fail": "Falha ao redefinir senha.",
    "admin.reactivated": "Integrante reativado.",
    "admin.deactivated": "Integrante desativado.",
    "admin.status_change_fail": "Falha ao alterar status.",
    "admin.field_name": "Nome",
    "admin.field_password": "Senha",
    "admin.tab_positions": "Positions",
    "admin.new_position": "Nova Position",
    "admin.edit_position": "Editar Position",
    "admin.position_name": "Nome da position",
    "admin.section_position_core": "Position (dados essenciais)",
    "admin.section_employee_profile": "Team member Profile",
    "admin.section_employee_profile_desc": "Perfil do integrante (selecione as opções que se aplicam)",
    "admin.section_job_profile": "Job Profile",
    "admin.section_job_profile_desc": "Descritivo detalhado da posição e vínculo com matriz salarial",
    "admin.section_access_profile": "Access Profile",
    "admin.section_access_profile_desc": "Perfil de acesso: sistemas, módulos e pastas visíveis",
    "admin.section_equipment": "Equipamentos",
    "admin.dimension_behavioral": "Comportamental",
    "admin.dimension_education": "Formação",
    "admin.dimension_experience": "Experiência",
    "admin.dimension_seniority": "Senioridade",
    "admin.dimension_base": "Base",
    "admin.responsibilities": "O que o integrante é responsável por",
    "admin.responsibilities_hint": "Indicadores, metas e resultados esperados",
    "admin.function_desc": "Função do integrante (dia a dia)",
    "admin.function_desc_hint": "O que executa no dia a dia — tarefas e rotinas",
    "admin.salary_matrix": "Matriz Salarial",
    "admin.salary_matrix_none": "Sem matriz",
    "admin.salary_quadrants": "Quadrantes aplicáveis (múltiplos)",
    "admin.salary_quadrants_hint": "Clique nos quadrantes da matriz para marcá-los como aplicáveis a esta position",
    "admin.matrix_track": "Trilha",
    "admin.selected": "Selecionado",
    "admin.quadrants_selected": "{n} quadrante(s) selecionado(s)",
    "admin.responsible_dept": "Depto responsável",
    "admin.equipment_dept_hint": "Vincule cada equipamento a um departamento responsável. Ao contratar um integrante, será criada uma tarefa de onboarding com o departamento indicado.",
    "admin.policy_qbo_account": "Chart of Accounts (QBO) para esta despesa",
    "admin.policy_qbo_account_hint": "Todo lançamento desta categoria irá para esta conta contábil no QBO ao ser sincronizado.",
    "admin.policy_qbo_no_accounts": "Sincronize o Chart of Accounts em QBO Sync primeiro",
    "admin.access_starter_btn": "Aplicar template",
    "admin.access_starter_confirm": "Substituir a árvore atual pelo template padrão?",
    "admin.hard_skills": "Hard skills obrigatórias",
    "admin.soft_skills": "Skills comportamentais",
    "admin.add_custom_skill": "Adicionar personalizada",
    "admin.multi_select_hint": "Múltipla escolha — clique para marcar/desmarcar",
    "admin.behavioral_descriptions": "Descrição dos perfis comportamentais",
    "admin.edit_lists": "Editar listas",
    "admin.equipment_hint": "Marque os equipamentos que a position requer",
    "admin.new_item": "Novo item",
    "admin.access_profile_tree": "Árvore de permissões (Sistemas → Módulos → Pastas)",
    "admin.new_system": "Novo sistema",
    "admin.new_module": "Novo módulo",
    "admin.new_folder": "Nova pasta",
    "admin.system_name": "Nome do sistema",
    "admin.module_name": "Nome do módulo",
    "admin.folder_name": "Nome da pasta",
    "admin.multi_payroll_companies": "Payroll Companies (múltiplas)",
    "admin.saved": "Salvo",
    "admin.rule_delete_confirm": "Excluir esta regra?",
    "admin.contract_optional": "Tipo de contrato (opcional)",
    "admin.all_contracts": "Todos os contratos",
    "admin.pto_carry_max": "Máx. carry-over de PTO",
    "admin.vac_carry_max": "Máx. carry-over de Férias",
    "admin.cc_delete_confirm": "Excluir este código de custo?",
    "admin.new_cost_code": "Novo código de custo",
    "admin.sup_delete_confirm": "Excluir este fornecedor?",
    "admin.new_supplier": "Novo fornecedor",
    "admin.sup_contact_ph": "telefone, e-mail ou site",
    "admin.adjust_reason_ph": "ex.: ajuste de fim de ano, exceção aprovada pelo CEO",
    "admin.by_manager": "Por gestor",
    "admin.search_users_ph": "Buscar por nome, e-mail, cargo, cost code…",
    "admin.all_roles": "Todos os perfis",
    "admin.all_depts": "Todos os departamentos",
    "admin.no_users_filters": "Nenhum integrante encontrado com esses filtros.",
    "admin.no_changes": "Nenhuma alteração para salvar.",
    "admin.user_updated": "Integrante atualizado.",
    "admin.pwd_min": "A senha nova precisa ter no mínimo 6 caracteres.",
    "admin.pwd_reset_done": "Senha redefinida. O integrante precisará fazer novo login (todos os tokens antigos foram invalidados).",
    "admin.deactivate_confirm": "Desativar {name}? Ele/ela perderá o acesso imediatamente.",
    "admin.edit_employee": "Editar integrante",
    "admin.pto_balance_days": "Saldo PTO (dias)",
    "admin.vac_balance_days": "Saldo Férias (dias)",
    "admin.deactivate": "Desativar",
    "admin.reset_password": "Redefinir senha",
    "admin.reset_pwd_hint": "Só admin. Invalida todos os tokens JWT antigos do integrante.",
    "admin.new_pwd_ph": "Nova senha (mín. 6 caracteres)",
    "admin.after_create_qbo": "Depois de criar, vá em",
    "admin.after_create_qbo2": "nessa linha para criar o Vendor no QuickBooks.",
    "admin.manager_hiring": "Gestor (hiring manager)",
    "admin.new_profile": "Novo perfil",
    "admin.no_components": "Nenhum componente. Adicione um ou escolha um preset.",
    "admin.amount_usd": "Valor (USD)",
    "admin.add_component": "Adicionar componente",
    "admin.new_chain": "Nova cadeia",
    "admin.unlimited_ph": "deixe em branco para ilimitado",
    "admin.demo_reset": "Script de reset da demo",
    "admin.slide_deck": "Apresentação para o cliente / novos integrantes",
    "admin.download_pptx": "Baixar .PPTX",
    "admin.download_pdf": "Baixar .PDF",
    "admin.project_delete_confirm": "Excluir projeto \"{code} · {name}\"? Isso não pode ser desfeito.",
    "admin.new_project": "Novo projeto",
    "admin.email_transport": "Transporte de e-mail:",
    "admin.pto_reminder_hours": "Lembrete de PTO pendente após (horas)",
    "admin.eval_reminder_days": "Lembrete de avaliação pendente (dias antes do fim do ciclo)",
    "admin.view_outbox": "Ver caixa de saída",
    "admin.new_policy": "Nova política",
    "admin.policy_delete_type_confirm": "Excluir o tipo \"{cat}\"? Despesas já lançadas com ele NÃO são apagadas; apenas o tipo deixa de aparecer em novos lançamentos.",
    "admin.delete_type": "Excluir tipo",
    "admin.internal_key": "Chave interna (minúsculas, sem espaço)",
    "admin.cat_label_ph": "ex.: Refeições",
    "admin.calc_mode": "Modo de apuração",
    "admin.calc_per_item": "Apurar por item",
    "admin.calc_per_day": "Apurar por dia",
    "admin.calc_per_trip": "Apurar por viagem (× dias)",
    "admin.policy_notes_ph": "Ex.: exige nota fiscal acima de $50; jantar com cliente liberado mediante aprovação prévia…",
    "admin.menuvis_desc": "Visibilidade do menu lateral por perfil.",
    "admin.menuvis_hide": "ocultar",
    "admin.menuvis_hint": "o item para aquele tipo de usuário. Útil para lançamentos por ondas.",
    "admin.menuvis_check": "Marque um perfil para",
    "admin.menuvis_admin_note": "Admin sempre vê tudo.",
    "admin.show_all": "Mostrar p/ todos",
    "admin.hide_all": "Ocultar p/ todos",
    "pay.total_value": "Valor total",
    "pay.total_due": "Total a pagar",
    "pay.gen_invoices_confirm": "Gerar invoices para {type} no período {start} → {end}?",
    "pay.payroll_type": "Tipo de folha",
    "pay.start": "Início",
    "pay.dist_project": "Distribuição por projeto (uma invoice por projeto)",
    "pay.dist_employee": "Distribuição por integrante (uma invoice por PJ)",
    "pay.composition": "Composição do pagamento",
    "pay.summary_emp": "Resumo por integrante",
    "pay.all_types": "Todos os tipos",
    "pay.no_invoices": "Nenhuma invoice gerada ainda.",
    "ts.subtitle": "Lance suas horas com rateio entre projetos e departamentos",
    "ts.period_start": "Período início",
    "ts.period_end": "Período fim",
    "ts.no_rows": "Nenhuma linha. Adicione uma alocação abaixo.",
    "ts.save_draft": "Salvar rascunho",
    "ts.submit_approval": "Enviar para aprovação",
    "ts.history": "Histórico de timesheets",
    "ts.no_periods": "Nenhum período registrado.",
    "perf.subtitle": "Avaliações de competência e planos de melhoria",
    "perf.self_ref": "Autoavaliação do integrante (referência)",
    "perf.tab_pending": "A avaliar (pendentes)",
    "perf.tab_team": "Meu time (todos)",
    "perf.tab_all": "Todos (RH)",
    "perf.tab_config": "Configuração",
    "perf.new_cycle": "Novo ciclo",
    "perf.clear_filter": "Limpar filtro",
    "perf.new_pip": "Novo PIP",
    "perf.no_pips": "Nenhum PIP.",
    "perf.add_goal": "Adicionar meta",
    "perf.no_followups": "Nenhum acompanhamento ainda.",
    "perf.close_pip": "Encerrar PIP",
    "perf.new_followup": "Novo acompanhamento",
    "perf.new_end_date": "Nova data final",
    "perf.del_eval_tooltip": "Excluir esta avaliação",
    "perf.del_eval_confirm": "Excluir a avaliação de {name} neste ciclo? Todo o progresso é perdido.",
    "perf.eval_deleted": "Avaliação excluída.",
    "perf.evals_created": "{added} avaliação(ões) criada(s)",
    "perf.evals_skipped": "{skipped} já existiam",
    "perf.add_people": "Adicionar pessoas ao ciclo",
    "perf.cycle_draft_created": "Ciclo criado como rascunho. Vá até a lista para Ativar.",
    "perf.cycle_name_ph": "Ex.: Avaliação 2026-H1",
    "perf.no_groups_yet": "Você ainda não cadastrou grupos. Vá até",
    "perf.search_ph": "Buscar por nome, e-mail, cargo…",
    "perf.mark_visible": "Marcar visíveis",
    "perf.role_label": "Função:",
    "perf.no_visible": "Nenhum integrante visível com os filtros atuais.",
    "perf.no_dept": "Sem departamento",
    "perf.remove_q_confirm": "Remover a pergunta \"{name}\"?",
    "perf.q_removed": "Pergunta removida.",
    "perf.group_removed": "Grupo removido.",
    "perf.groups_title": "Grupos de avaliação",
    "perf.new_group": "Novo grupo",
    "perf.groups_hint": "Ex.: Liderança, Cultura, Técnico. Cada pergunta se vincula a 1 grupo.",
    "perf.no_groups": "Nenhum grupo cadastrado ainda.",
    "perf.new_question": "Nova pergunta",
    "perf.questions_hint": "Cada pergunta é um item avaliável (1-5). Adicione quantas quiser dentro de cada grupo.",
    "perf.no_questions": "Nenhuma pergunta.",
    "perf.group_name_ph": "Ex.: Liderança",
    "perf.group_desc_ph": "Curta descrição do tema",
    "perf.q_ph": "Ex.: Sabe delegar tarefas com clareza",
    "perf.q_help": "Descrição / ajuda",
    "perf.q_help_ph": "Contexto opcional visível ao avaliador",
    "sal.access_denied": "A gestão salarial é acessível apenas a RH, admin e CEO.",
    "sal.title": "Matriz salarial",
    "sal.no_matrix": "Nenhuma matriz configurada ainda",
    "sal.create_matrix": "Criar matriz",
    "sal.view_history": "Ver histórico",
    "sal.edit_matrix": "Editar matriz salarial",
    "sal.edit_matrix_hint": "Configure trilhas (linhas), níveis (colunas) e faixas salariais por célula.",
    "sal.track_name_ph": "Nome da trilha",
    "sal.add_track": "Adicionar trilha",
    "sal.level_name_ph": "Nome do nível (ex.: I, II, Sênior)",
    "sal.add_level": "Adicionar nível",
    "sal.min_ph": "mín",
    "sal.max_ph": "máx",
    "sal.move_queued": "Movimentação enviada para aprovação — próximo aprovador: {role}. A mudança só é aplicada após toda a cadeia aprovar.",
    "sal.effective_date": "Data de vigência",
    "sal.no_history": "Sem histórico ainda.",
    "sal.edit_meta": "Editar metadados",
    "sal.edit_values": "Editar valores",
    "sal.new_matrix": "Nova matriz",
    "sal.no_contract": "Sem contrato definido (—)",
    "sal.delete_matrix_confirm": "Deletar a matriz \"{name}\"? Só é possível se não houver ocupantes.",
    "sal.all_contract_types": "todos os tipos de contrato",
    "sal.delete_matrix": "Deletar matriz",
    "sal.salary_history": "Histórico salarial",
    "sal.no_moves": "Sem movimentos registrados.",
    "sal.search_ph": "Buscar por nome, cargo, matriz…",
    "sal.all_roles": "Todos os cargos",
    "sal.only_allocated": "Só alocados",
    "sal.only_unallocated": "Só não alocados",
    "sal.no_emp_filters": "Nenhum integrante com esses filtros.",
    "sal.unallocated": "Não alocado",
    "train.mgr_suggested": "Sugerido pelo gestor",
    "train.delete_confirm": "Excluir treinamento \"{name}\"?",
    "train.new": "Novo treinamento",
    "train.open_course": "Abrir curso",
    "train.mandatory_roles": "Obrigatório para os perfis",
    "train.min_days_ph": "ex.: 90 (só após 90 dias de empresa)",
    "train.due_date": "Data limite",
    "train.note_emp": "Observação para o integrante (opcional)",
    "train.no_assignments": "Nenhuma atribuição.",
    "train.no_mandatory": "Nenhum treinamento obrigatório configurado.",
    "train.no_team": "Nenhum membro na equipe.",
    "train.subtitle": "Trilhas obrigatórias, sugeridas e atribuídas pelo gestor",
    "train.team_status": "Status da equipe",
    "hire.observe_ph": "O que você observou? Por que essa transição?",
    "hire.no_equipment": "Nenhum equipamento definido",
    "hire.new_vacancy": "Nova vaga",
    "hire.position_select": "Position (obrigatório)",
    "hire.custom_fill": "Custom (preencher manualmente abaixo)",
    "hire.picking_hint": "Escolher uma Position auto-preenche departamento, cargo, payroll company, access profile, equipamentos, skills, matriz e payment.",
    "hire.job_title": "Título da vaga",
    "hire.no_locations": "Cadastre localidades em Rules by Location primeiro",
    "hire.select_location_first": "Selecione a localidade primeiro",
    "hire.access_profile": "Access profile",
    "hire.select": "Selecionar",
    "hire.position_snapshot": "Snapshot da Position (confirme ou edite)",
    "hire.position_snapshot_hint": "Estas informações foram copiadas da Position selecionada. Ajuste o que for específico desta vaga — o cadastro-mestre da Position não será alterado.",
    "hire.chip_add": "Adicionar",
    "hire.chip_close": "Fechar",
    "hire.chip_new_option_ph": "Nova opção…",
    "hire.change_stage": "Alterar etapa com comentário",
    "hire.move_stage_title": "Mudar etapa do candidato",
    "hire.new_stage": "Nova etapa",
    "hire.tab_scoreboard": "Placar",
    "hire.tab_pipeline": "Pipeline",
    "hire.tab_vacancies": "Vagas",
    "hire.filter_status": "Status",
    "hire.filter_department": "Departamento",
    "hire.filter_vacancy": "Vaga",
    "hire.filter_position": "Position",
    "hire.filter_priority": "Prioridade",
    "hire.filter_all": "Todos",
    "hire.filter_search_candidate": "Buscar candidato pelo nome…",
    "hire.reset_filters": "Limpar filtros",
    "hire.funnel_empty_stage": "Vazio",
    "hire.edit_vacancy": "Editar",
    "hire.delete_vacancy": "Excluir",
    "hire.delete_confirm": "Tem certeza que deseja excluir esta vaga? Esta ação não pode ser desfeita.",
    "hire.no_candidates_filter": "Nenhum candidato encontrado com esses filtros.",
    "hire.candidates_shown": "{shown} de {total} candidatos",
    "hire.scoreboard_title": "Placar de Recrutamento",
    "hire.scoreboard_hint": "Visão consolidada de vagas, candidatos e performance do pipeline.",
    "hire.kpi_open_vacancies": "Vagas abertas",
    "hire.kpi_active_candidates": "Candidatos ativos",
    "hire.kpi_hired": "Contratados",
    "hire.kpi_offer_acceptance": "Aceite de proposta",
    "hire.by_stage": "Por etapa",
    "hire.avg_days_in_stage": "Dias médios por etapa",
    "hire.top_interviewers": "Top entrevistadores",
    "hire.vacancy_leaderboard": "Vagas com mais candidatos",
    "hire.approved_of_assigned": "{approved} aprovados / {assigned} designados",
    "hire.stage_approved": "Aprovado",
    "hire.assigned_interviewer": "Entrevistador designado",
    "hire.pick_interviewer": "Selecione o entrevistador",
    "hire.pick_interviewer_required": "Você precisa escolher um entrevistador antes de mover para Interview.",
    "hire.only_assigned_can_move": "Somente o entrevistador designado pode mover este candidato.",
    "hire.forward_to_interviewer": "Encaminhar para outro entrevistador",
    "hire.move_to_approved": "Enviar para Aprovado",
    "hire.approve_title": "Confirmar informações da vaga",
    "hire.approve_hint": "Confirme os dados que serão gravados na Offer Letter e no perfil do integrante. Ajuste o que foi negociado.",
    "hire.confirm_approval": "Confirmar aprovação",
    "hire.negotiated_salary": "Salário negociado",
    "hire.salary_pick_cell": "Escolha a célula da matriz (Track × Level)",
    "hire.salary_within_band": "Dentro da faixa",
    "hire.salary_outside_band": "Fora da faixa {min}–{max}",
    "hire.confirmed_offer": "Oferta confirmada",
    "hire.send_offer_letter": "Enviar Offer Letter",
    "hire.mark_offer_signed": "Marcar como Offer Signed",
    "hire.approved_summary": "Resumo da aprovação",
    "hire.reassign_interviewer_hint": "Após aprovar, o RH pega o candidato desta lista e envia a Offer Letter.",
    "hire.payment_format": "Formato de pagamento",
    "hire.payment_amount": "Valor",
    "hire.allowed_band": "Faixa salarial permitida",
    "hire.quadrants_short": "quadrante(s)",
    "hire.out_of_range": "Fora da faixa {min}–{max} definida pela matriz para os quadrantes selecionados",
    "hire.job_desc": "Descrição da vaga",
    "hire.create_draft": "Criar rascunho",
    "hire.job_profile_opt": "Aplicar um perfil de cargo padrão (opcional)",
    "hire.reports_to": "Reporta a (gestor)",
    "hire.target_start": "Data-alvo de início",
    "hire.new_applicant": "Novo candidato",
    "hire.hire_create": "Contratar & criar usuário",
    "hire.initial_pwd_ph": "Senha inicial",
    "hire.add_candidate": "Adicionar candidato",
    "hire.equipment_needed": "Equipamentos necessários",
    "hire.title": "Contratações & Admissão",
    "offb.finalize_confirm": "Finalizar? O usuário será desativado.",
    "offb.revoke_it": "Revogar acessos de TI",
    "offb.finalize_btn": "Finalizar desligamento & desativar usuário",
    "offb.primary_reason": "Principal motivo da saída",
    "offb.new_termination": "Novo desligamento",
    "offb.none": "Nenhum desligamento.",
    "onb.no_pending": "Nenhum onboarding pendente",
    "onb.no_tasks": "Você não tem tarefas de onboarding atribuídas.",
    "onb.required_steps": "Etapas obrigatórias",
    "onb.mark_done": "Marcar concluído",
    "kn.upload_hint": "PDF, DOCX, TXT, MD. Máx. 50 MB.",
    "kn.paste_text_ph": "Opcional: cole o texto extraído aqui para o Sage buscar no conteúdo deste documento",
    "kn.sharepoint_ph": "Nome do site SharePoint (opcional)",
    "kn.paste_summary_ph": "Cole/resuma o conteúdo do documento aqui para o conhecimento do Sage",
    "kn.active_visible": "Ativo (visível para o Sage Copilot)",
    "kn.subtitle": "Documentos e políticas da empresa que alimentam o Sage Copilot",
    "kn.new_doc": "Novo documento",
    "kn.search_ph": "Buscar documentos, tags, conteúdo...",
    "kn.no_docs": "Nenhum documento encontrado.",
    "kn.total_questions": "Total de perguntas",
    "kn.open_gaps": "Lacunas abertas",
    "kn.key_saved": "Salvo. O Sage passará a usar sua chave.",
    "kn.key_reset_confirm": "Restaurar a Universal Key da Emergent?",
    "kn.reset_emergent": "Restaurar Emergent",
    "plan.subtitle": "Sua agenda, prazos e lembretes em um só lugar",
    "plan.new_reminder": "Novo lembrete",
    "plan.view": "Visualização:",
    "plan.list": "Lista",
    "plan.search_ph": "Buscar por nome…",
    "plan.all_employees": "Todos os integrantes",
    "plan.no_events": "Nenhum evento neste período.",
    "plan.delete_reminder": "Excluir este lembrete?",
    "plan.cycle": "Ciclo:",
    "plan.for_employee": "Para integrante (opcional)",
    "dh.title": "Hub de departamentos",
    "dh.subtitle": "Diagramas e quizzes de integração para cada equipe",
    "dh.no_diagram": "Sem diagrama ainda",
    "dh.not_configured": "Não configurado",
    "dh.no_diagram_dept": "Nenhum diagrama configurado para este departamento.",
    "dh.edit_diagram": "Editar diagrama",
    "dh.edit_quiz": "Editar quiz",
    "dh.quiz_min": "Adicione pelo menos uma pergunta válida com 2+ opções.",
    "dh.question_ph": "Texto da pergunta",
    "dh.add_option": "Adicionar opção",
    "dh.add_question": "Adicionar pergunta",
    "cop.feedback_ph": "O que estava errado nesta resposta? (ajuda o RH a corrigir a lacuna de conhecimento)",
    "cop.delete_conv": "Excluir esta conversa?",
    "cop.subtitle": "Seu assistente de IA para processos e políticas da empresa",
    "cop.new_chat": "Nova conversa",
    "cop.no_convs": "Nenhuma conversa ainda.",
    "docs.upload_url": "URL do documento ou upload",
    "man.sec_workspace_desc": "Recursos para seu dia a dia",
    "man.sec_people_desc": "Conheça e gerencie seu time",
    "man.sec_ops_desc": "Aprovar, consultar, pagar",
    "man.sec_system_desc": "Personalização e perfil",
    "pto.no_comments": "Nenhum comentário ainda. Inicie a conversa abaixo.",
    "reimb.select_project": "Selecionar projeto...",
    "reimb.select_cost_code": "Selecionar código de custo...",
    "reimb.select_supplier": "Selecionar fornecedor...",
    "reimb.employee_vendor_qbo": "Vendor do integrante (QBO)",
    "reimb.no_qbo_log": "Nenhum registro de log QBO.",
    "reimb.date_value_required": "Data e valor são obrigatórios.",
    "reimb.request_currency": "Moeda (aplica a todos os itens)",
    "reimb.request_currency_help": "Todos os itens desta submissão serão registrados nesta moeda.",
    "reimb.confirm_delete": "Excluir este rascunho?",
    "reimb.confirm_admin_delete": "Isso moverá o reembolso para a lixeira. Somente admins podem restaurar. Continuar?",
    "reimb.admin_delete_btn": "Excluir (admin)",
    "reimb.show_deleted": "Mostrar lixeira (admin)",
    "reimb.next_step_redteam": "Próximo passo: RedTeam",
    "reimb.redteam_after": "Após confirmar, o sistema dispara a integração QuickBooks automaticamente.",
    "qbo.search_catalog_ph": "Buscar (nome, tipo, subtipo)…",
    "qbo.search_ph": "Buscar…",
    "qbo.create_vendors_confirm": "Criar vendor no QBO para TODOS os integrantes ainda sem qbo_vendor_id?",
    "qbo.sync_result": "Sincronizados: {ok} · Falharam: {fail}",
    "qbo.display_name": "Nome de exibição",
    "qbo.categories_coa": "Categorias (Plano de Contas)",
    "brand.primary": "Primária",
    "brand.secondary": "Secundária",
    "brand.success": "Sucesso",
    "brand.logo_mark": "Logo (ícone quadrado)",
    "brand.google_fonts": "Carregada via Google Fonts. Para fonte customizada, contate o Super Admin.",
    "brand.button": "Botão",
    "brand.approved": "Aprovado",
    "brand.pending": "Pendente",
    "brand.rejected": "Rejeitado",
    "brand.updated": "Branding atualizado e aplicado.",
    "brand.url_ph": "URL (ou faça upload)",
    "brand.too_big": "Arquivo maior que 10MB",
    "brand.uploaded": "Logo enviado",
    "sa.list_fail": "Falha ao listar tenants",
    "sa.subtitle": "Gerencie tenants, branding e usuários de cada empresa.",
    "sa.users_col": "Usuários",
    "sa.no_companies": "Nenhuma empresa cadastrada ainda.",
    "sa.delete_company": "Excluir empresa",
    "sa.irreversible": "Esta ação é irreversível.",
    "sa.company_label": "Empresa:",
    "sa.users_delete": "Usuários a deletar:",
    "sa.type_slug": "Para confirmar, digite o slug:",
    "sa.type_exact": "Digite \"{slug}\" exatamente para confirmar",
    "sa.company_deleted": "Empresa \"{name}\" excluída",
    "sa.new_company": "Nova empresa",
    "sa.slug_hint": "Letras minúsculas, números, traços. Usado internamente, não muda mais depois.",
    "sa.slug_name_required": "Slug e nome são obrigatórios",
    "sa.company_created": "Empresa criada",
    "sa.company_updated": "Empresa atualizada",
    "sa.fill_admin": "Preencha e-mail, nome e senha",
    "sa.admin_created": "Admin criado: {email}",
    "sa.company_active": "Empresa ativa (desmarque para suspender o acesso de todos)",
    "sa.font_hint": "A fonte deve estar disponível via Google Fonts (já carregada). Para customizadas, fale com o suporte.",
    "sa.create_admin": "Criar 1º Admin",
    "sa.action_button": "Botão de ação",
    "cockpit.ev_vacation": "Férias",
    "cockpit.ev_birthday": "Aniversário",
    "cockpit.loading_calendar": "Carregando calendário…",
    "cockpit.no_events_120": "Nenhum evento previsto para os próximos 120 dias.",
    "cockpit.loading_timeline": "Carregando linha do tempo…",
    "cockpit.no_activity_60": "Sem atividade da equipe nos últimos 60 dias.",
    "cockpit.critical": "Crítico",
    "cockpit.attention": "Atenção",
    "cockpit.loading_risk": "Carregando indicadores de risco…",
    "admin.edit_cost_code": "Editar código de custo",
    "admin.edit_supplier": "Editar fornecedor",
    "admin.no_policies": "Nenhuma política criada ainda — clique em \"Nova política\" para começar.",
    "admin.edit_project": "Editar projeto",
    "admin.no_projects": "Nenhum projeto — clique em \"Novo projeto\" para criar.",
    "admin.add_approver": "+ Adicionar perfil aprovador…",
    "admin.tiers": "Faixas",
    "admin.account_status": "Status da conta",
    "admin.active_login": "Ativa · pode fazer login",
    "admin.deactivated_login": "Desativada · login bloqueado, JWTs revogados no próximo acesso",
    "admin.reactivate": "Reativar",
    "admin.creating": "Criando...",
    "admin.create_employee": "Criar integrante",
    "admin.qbo_hint_click": "e clique em",
    "admin.qbo_hint_path": "QBO Sync & Mapping → Integrantes",
    "admin.corp_notif_desc": "Estas regras valem para toda a empresa. O RH pode ajustar os limites e o interruptor mestre de e-mails.",
    "admin.enabled_stub": "ATIVADO (stub)",
    "admin.disabled_label": "DESATIVADO",
    "admin.email_note_title": "Nota:",
    "admin.email_note": "O provedor de e-mail (Resend/SendGrid) ainda não está conectado. O motor de regras e a caixa de saída funcionam; ativar \"Enviar e-mails\" grava as mensagens na caixa de saída de auditoria. Quando um provedor for escolhido, conectamos sem alterar estas regras.",
    "admin.would_send": "mensagem(ns) seriam enviadas. Enviar e-mails:",
    "admin.outbox_empty": "Caixa de saída vazia. Rode com \"Enviar e-mails\" ativado para popular.",
    "admin.menuvis_reload": "Após salvar, peça aos usuários para recarregar a página (ou verão a mudança no próximo login).",
    "travel.new_expense": "Nova despesa",
    "qbo.coa_hint1": "Selecione a conta (ou subconta) do Plano de Contas do QBO para cada categoria. No envio do Bill, a linha vai como",
    "qbo.coa_hint2": "apontando para o",
    "qbo.coa_hint3": "escolhido. Se uma categoria ficar sem mapeamento, usamos a conta de despesa padrão.",
    "status.on_track": "No caminho",
    "status.needs_improvement": "Precisa melhorar",
    "status.off_track": "Fora do caminho",
    "perf.your_rating": "Sua nota",
    "perf.rating_scale": "1=Abaixo · 3=Atende · 5=Excede",
    "sal.awaiting": "Aguardando decisão de",
    "sal.applicability": "Aplicabilidade",
    "sal.applies_all_1": "Aplica a",
    "pay.close": "Fechar",
    "hire.stage_applied": "Inscrito",
    "hire.stage_screening": "Triagem",
    "hire.stage_interview": "Entrevista",
    "hire.stage_offer_sent": "Proposta enviada",
    "hire.stage_offer_signed": "Proposta assinada",
    "hire.stage_hired": "Contratado",
    "hire.stage_rejected": "Rejeitado",
    "hire.move_to": "Mover para",
    "train.type_mandatory": "Obrigatório",
    "train.type_suggested": "Sugerido",
    "train.mandatory_for": "Obrigatório para",
    "train.cat_compliance": "Compliance",
    "train.cat_technical": "Técnico",
    "train.cat_leadership": "Liderança",
    "train.cat_safety": "Segurança",
    "train.cat_soft_skills": "Soft skills",
    "train.cat_onboarding": "Onboarding",
    "train.cat_other": "Outros",
    "cockpit.ev_return": "Retorno",
    "cockpit.ev_anniversary": "Anos de casa",
    "cockpit.info_label": "Aviso",
    "kn.sessions": "Sessões",
    "kn.satisfaction": "Taxa de satisfação",
    "kn.verified_qa": "Q&As verificadas",
    "kn.thumbs_up": "👍 Positivos",
    "kn.thumbs_down": "👎 Negativos",
    "kn.storage_hint": "Armazenamento local (migrará para S3/GCS).",
    "perf.submitted_compare": "Enviada em {date}. Compare suas notas abaixo com a autoavaliação exibida ao lado de cada competência.",
    "perf.click_fill_self": "→ Clique para preencher sua autoavaliação",
    "offb.open_vacancy": "Abrir vaga de substituição",
    "offb.submit_request": "Enviar solicitação",
    "offb.requested_by": "Solicitado por",
    "offb.would_recommend": "Recomendaria a Sagewood para outros",
    "offb.feedback_sug": "Feedback / sugestões",
    "offb.cases_summary": "{n} casos · {m} em andamento",
    "offb.revoked": "Revogado ·",
    "onb.optional": "Opcionais / recomendadas",
    "onb.auto_completes": "conclui automaticamente ao finalizar",
    "sal.target_cell": "Célula de destino",
    "sal.select_cell": "— selecione a célula —",
    "sal.band_hint": "Faixa: {min} – {max} · bônus-alvo {pct}% (definido pela célula)",
    "sal.new_salary": "Novo salário",
    "sal.out_of_band": "Salário {v} está FORA da faixa {min}–{max}. Confirme a exceção.",
    "train.title": "Treinamento & Desenvolvimento",
    "train.tab_mine": "Meus treinamentos",
    "train.tab_catalog": "Catálogo",
    "train.tab_compliance": "Compliance",
    "train.assign": "Atribuir",
    "train.assign_title": "Atribuir:",
    "travel.receipt_required": "Anexo do recibo é obrigatório para enviar à aprovação.",
    "travel.attach_receipt": "Anexar recibo",
    "travel.tap_upload": "Toque para enviar foto ou PDF",
    "travel.qbo_sync_title": "Sincronização QuickBooks",
    "travel.paid_qbo": "Pago no QBO",
    "travel.partial": "Parcial",
    "travel.awaiting_payment": "Aguardando pagamento",
    "travel.lines": "Linhas",
    "travel.dry_run_note": "MODO DRY-RUN — CHAMADAS QBO SIMULADAS",
    "perf.questions_by_group": "Perguntas por grupo",
    "perf.edit_group": "Editar grupo",
    "perf.edit_question": "Editar pergunta",
    "perf.group_created": "Grupo criado.",
    "perf.group_updated": "Grupo atualizado.",
    "perf.q_created": "Pergunta criada.",
    "perf.q_updated": "Pergunta atualizada.",
    "perf.q_comp_label": "Pergunta / competência",
    "perf.applies_roles": "Aplica-se aos perfis (deixe vazio = todos)",
    "common.order": "Ordem",
    "common.saving": "Salvando…",
    "common.birth_date": "Data de nascimento",
    "common.choose_file": "Escolher arquivo",
    "offb.click_toggle": "Clique em uma tarefa para alternar entre concluída ↔ pendente.",
    "offb.title": "Offboarding & Desligamento",
    "approvals.waiting_n": "{n} aguardando",
    "sal.applies_all_badge": "aplica a todos os contratos",
    "sal.employees_count": "Integrantes · {a} de {b}",
    "sal.people_n": "{n} pessoa(s)",
    "sal.new_matrix_full": "Nova matriz salarial",
    "sal.register": "Cadastrar",
    "sal.currency": "Moeda",
    "sal.active_picker": "Ativa (aparece no seletor)",
    "sal.matrix_name_ph": "Ex.: Sagewood · Brasil (PJ)",
    "pay.value": "Valor",
    "pay.end": "Fim",
    "pay.project": "Projeto",
    "kn.stats": "Estatísticas",
    "admin.policy_subtitle": "Defina limites por categoria de despesa de viagem. Apenas a política ativa é aplicada nos lançamentos dos integrantes.",
    "admin.only_admin": "(apenas Admin pode alterar)",
    "admin.configure_soon": "Configurar (em breve)",
    "qbo.save_selection": "Salvar seleção",
    "qbo.no_match_search": "Nenhuma conta corresponde à busca.",
    "qbo.no_accounts_synced": "Nenhuma conta sincronizada ainda — clique em Sincronizar no card acima.",
    "qbo.last_sync": "Última sync:",
    "qbo.never_synced": "Nunca sincronizado",
    "qbo.save_mapping": "Salvar mapping",
    "qbo.sync_first": "Sincronize Categorias (Chart of Accounts) e Classes acima antes de configurar o mapping.",
    "qbo.cat_to_account": "Categoria de despesa → QBO Account",
    "qbo.not_mapped": "— não mapeado —",
    "qbo.employees_to_vendor": "Integrantes → QBO Vendor",
    "qbo.not_synced": "— não sincronizado —",
    "qbo.sync_all": "Sincronizar todos",
    "qbo.syncing": "Sincronizando…",
    "qbo.categories": "Categorias",
    "brand.subtitle": "Personalize logo, paleta de cores, tagline e fonte. As mudanças se refletem em todo o portal imediatamente.",
    "brand.palette": "Paleta de cores",
    "brand.logo_font": "Logo · Fonte",
    "brand.font": "Fonte",
    "brand.live_preview": "Preview ao vivo",
    "perf.questions_n": "{n} pergunta(s)",
    "perf.questions_selected": "{n} pergunta(s) selecionada(s)",
    "perf.remove_group_confirm": "Remover \"{name}\"? As {count} pergunta(s) desse grupo ficarão sem grupo (você pode reatribuir depois).",
    "sal.all_chip": "todos",
    "qbo.depara_title": "De-para — Despesas de viagem → QBO",
    "qbo.dept_to_class": "Departamento (Cost Center) → QBO Class",
    "qbo.employees_n": "{n} integrantes",
  },
  en: {
    // ─── App & landing
    "app.name": "Welcome Home, Sagewood Team",
    "app.tagline": "Your people portal. Everything in one place.",
    "app.cta": "Enter the portal",
    "landing.badge": "People & Organization · Sagewood",
    "landing.feat_pto": "PTO & Vacation",
    "landing.feat_pto_desc": "Request, track and approve everything in a single flow.",
    "landing.feat_reimb": "Reimbursements",
    "landing.feat_reimb_desc": "Overhead and Project Costs with automatic invoicing.",
    "landing.feat_dir": "Directory",
    "landing.feat_dir_desc": "Find your colleagues, departments and managers.",
    "landing.feat_org": "Org Chart",
    "landing.feat_org_desc": "See the full Sagewood structure at a glance.",
    "landing.footer": "Sagewood · People & Organization Portal",
    // ─── Navigation / sidebar
    "nav.dashboard": "Home",
    "nav.org": "Org Chart",
    "nav.directory": "Directory",
    "nav.pto": "Time Off & PTO",
    "nav.reimb": "Reimbursements",
    "nav.timesheet": "Timesheet",
    "nav.travel": "Travel",
    "nav.expenses": "Expenses",
    "nav.loans": "Loans",
    "nav.docs": "Documents",
    "nav.approvals": "Approvals",
    "nav.profile": "My Profile",
    "nav.admin": "Admin & HR",
    "nav.manual": "User manual",
    "nav.reports": "Report Center",
    "reports.page_sub": "Request, approve and download portal data reports",
    "reports.tab_mine": "My reports",
    "reports.tab_approvals": "Approvals",
    "reports.tab_governance": "Governance",
    "reports.new_request": "New request",
    "reports.status_pending_approval": "Awaiting HR",
    "reports.status_approved": "Active",
    "reports.status_rejected": "Rejected",
    "reports.status_revoked": "Revoked",
    "reports.status_expired": "Expired",
    "reports.freq_once": "One-time",
    "reports.freq_weekly": "Weekly",
    "reports.freq_monthly": "Monthly (1st, 5am)",
    "reports.freq_quarterly": "Quarterly (quarter start)",
    "reports.freq_yearly": "Yearly (Jan 1st)",
    "reports.validity_once": "One-time use (available for 30 days)",
    "reports.validity_months": "For months",
    "reports.validity_years": "For years",
    "reports.validity_permanent": "Permanent",
    "reports.run_now": "Generate now",
    "reports.download_xlsx": "Excel",
    "reports.download_csv": "CSV",
    "reports.next_run": "Next run",
    "reports.expires": "Expires",
    "reports.never_expires": "No expiration",
    "reports.runs_history": "Runs",
    "reports.no_reports": "No reports yet. Click \"New request\" to ask for your first one.",
    "reports.no_pending": "No pending requests.",
    "reports.step_entities": "What do you need?",
    "reports.step_fields": "Which columns?",
    "reports.step_filters": "Filters & period",
    "reports.step_schedule": "Frequency & validity",
    "reports.step_review": "Review & justification",
    "reports.wizard_title": "Request new report",
    "reports.justification": "Justification (why do you need it?)",
    "reports.title_label": "Report name",
    "reports.title_ph": "e.g. Headcount by department",
    "reports.mode_detail": "Detailed",
    "reports.mode_summary": "Summary (grouped)",
    "reports.group_by": "Group by",
    "reports.metrics": "Metrics",
    "reports.sens_restricted": "Restricted",
    "reports.sens_confidential": "Confidential",
    "reports.confidential_warning": "This request includes confidential data — HR must approve it explicitly.",
    "reports.approve": "Approve",
    "reports.reject": "Reject",
    "reports.revoke": "Revoke",
    "reports.preview": "Preview sample (up to 10 rows)",
    "reports.removed_fields_note": "Columns removed by HR",
    "reports.requester": "Requester",
    "reports.approved_by": "Approved by",
    "reports.rejected_reason": "Rejection reason",
    "reports.notif_pending_title": "Report request to approve",
    "reports.notif_approved_title": "Your report was approved",
    "reports.notif_rejected_title": "Your report was rejected",
    "reports.notif_run_ready_title": "New report execution ready",
    "reports.save_as_template": "Save as template",
    "reports.save_as_template_hint": "The approved report (with the columns you keep) becomes a template other requesters can use as a starting point.",
    "reports.template_name": "Template name",
    "reports.template_description": "Description (optional)",
    "reports.template_applied": "Template applied — tweak anything you need before submitting.",
    "reports.template_delete_confirm": "Delete this template? Requests already made from it aren't affected.",
    "reports.start_from_template": "Start from a template",
    "reports.start_from_template_hint": "Templates are HR-approved reports — click one to prefill the wizard.",
    "reports.global_filters": "Global filters",
    "reports.global_filters_hint": "Automatically applied to entities that expose the matching field (department, role, date).",
    "reports.applied": "applied",
    "reports.period": "Period",
    "reports.period_none": "No period filter",
    "reports.period_custom": "Custom dates",
    "reports.departments": "Departments",
    "reports.no_departments": "No departments set up yet.",
    "reports.roles": "Roles",
    "reports.err_title_required": "Give the report a name.",
    "reports.err_entity_required": "Pick at least one entity.",
    "reports.err_no_fields": "Pick at least one column.",
    "reports.err_no_groupby": "In summary mode, choose a field to group by.",
    "reports.showing_of_total": "showing {n} of {total}",
    "reports.preview_empty_hint": "The report filters returned 0 rows. Check whether the period or the departments/roles list is too restrictive, or whether there is no data in this slice yet.",
    "reports.date_fixed": "Fixed range",
    "reports.date_rolling": "Rolling window",
    "reports.date_field": "Date field",
    "reports.no_filters": "No filters — returns everything",
    "reports.window_last_7d": "Last 7 days",
    "reports.window_last_30d": "Last 30 days",
    "reports.window_last_90d": "Last 90 days",
    "reports.window_current_month": "Current month",
    "reports.window_previous_month": "Previous month",
    "reports.window_current_quarter": "Current quarter",
    "reports.window_previous_quarter": "Previous quarter",
    "reports.window_ytd": "Year to date (YTD)",
    "reports.window_previous_year": "Previous year",
    "reports.submit_request": "Send for HR approval",
    "reports.rows": "rows",
    "reports.next": "Next",
    "reports.back": "Back",
    "reports.comment": "Comment",
    "reports.pending_hint": "Your request is with HR. You'll be notified once it's decided.",
    "reports.frequency": "Frequency",
    "reports.validity": "Access validity",
    "reports.trigger_scheduled": "Scheduled",
    "reports.trigger_on_demand": "On demand",
    "reports.trigger_approval": "On approval",
    "reports.gov_intro": "All report grants in this company.",
    "reports.select_fields_hint": "Confidential and restricted fields are unchecked by default.",
    "reports.weekday_label": "Weekday",
    "reports.run_failed": "Generation failed — try generating again",
    "reports.restricted": "You don't have permission to request reports. Ask HR to grant access.",
    "nav.cockpit": "Manager cockpit",
    "nav.logout": "Sign out",
    "nav.section.workspace": "Workspace",
    "nav.section.people": "People",
    "nav.section.operations": "Operations",
    "nav.section.system": "System",
    "nav.section.admin": "Administration",
    "nav.knowledge": "Knowledge",
    "nav.copilot": "Sage Copilot",
    "nav.performance": "Performance",
    "nav.hiring": "Hiring",
    "nav.offboarding": "Offboarding",
    "nav.salary": "Salary range",
    "nav.planner": "Planner",
    "nav.dept_hub": "Department Hub",
    "nav.alerts": "Alerts",
    "nav.notifications": "Notifications",
    // ─── Login
    "login.title": "Welcome back",
    "login.subtitle": "Access your company portal",
    "login.email": "Work email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.error": "Invalid credentials",
    "login.signing_in": "Signing in...",
    "login.password_show": "Show password",
    "login.password_hide": "Hide password",
    // ─── Dashboard
    "dash.greeting": "Hello",
    "dash.welcome": "Welcome back",
    "dash.pto_balance": "PTO Balance",
    "dash.vac_balance": "Vacation Balance",
    "dash.days": "days",
    "dash.my_requests": "My requests",
    "dash.pending_approvals": "Waiting your approval",
    "dash.quick_actions": "Quick actions",
    "dash.request_pto": "Request PTO/Vacation",
    "dash.submit_reimb": "Submit reimbursement",
    "dash.view_docs": "View documents",
    "dash.view_org": "View org chart",
    "dash.smart_alerts": "Smart Alerts",
    "dash.our_values": "Our values",
    "dash.welcome_chip": "Welcome home",
    "dash.more_alerts": "+ {n} more · click the bell at the top",
    // ─── Drafts banner
    "drafts.banner_title_one": "You have 1 draft pending correction",
    "drafts.banner_title_many": "You have {n} drafts pending correction",
    "drafts.banner_help": "click to edit and resubmit.",
    "drafts.count_reimb_one": "1 reimbursement",
    "drafts.count_reimb_many": "{n} reimbursements",
    "drafts.count_travel_one": "1 trip",
    "drafts.count_travel_many": "{n} trips",
    "drafts.cta_reimb": "Reimbursements",
    "drafts.cta_travel": "Travel",
    "drafts.notif_reimb_title": "Reimbursement returned as draft",
    "drafts.notif_travel_title": "Travel returned as draft",
    "drafts.summary_one": "1 reimbursement returned as draft. Click an item below (\u201cDraft\u201d badge) to edit and resubmit.",
    "drafts.summary_many": "{n} reimbursements returned as drafts. Click an item below (\u201cDraft\u201d badge) to edit and resubmit.",
    // ─── PTO
    "pto.title": "Time Off & PTO",
    "pto.new": "New request",
    "pto.type": "Type",
    "pto.start": "Start date",
    "pto.end": "End date",
    "pto.days": "Days",
    "pto.reason": "Reason / note",
    "pto.submit": "Submit request",
    "pto.history": "History",
    "pto.empty": "You have no requests yet.",
    "pto.balance_pto": "PTO Balance",
    "pto.balance_vac": "Vacation Balance",
    "pto.expires_in": "Expires in",
    "pto.expired": "EXPIRED",
    "pto.expires_soon": "Expires soon",
    "pto.my_rule": "Your applicable policy",
    "pto.rule_annual": "Annual grant",
    "pto.rule_carryover": "Max carry-over",
    "pto.rule_accrual": "Accrual",
    "pto.rule_probation": "Probation period",
    "pto.insufficient": "Insufficient balance for this request",
    // ─── Admin
    "admin.pto_mgmt": "PTO/Vacation Management",
    "admin.pto_rules": "Rules by Location",
    "admin.pto_overview_title": "Team balances",
    "admin.adjust_balance": "Adjust balance",
    "admin.adjust_reason": "Reason for adjustment",
    "admin.adjust_save": "Apply adjustment",
    "admin.new_rule": "New rule",
    "admin.edit_rule": "Edit rule",
    "admin.delete": "Delete",
    "admin.used_year": "Used this year",
    "admin.expiring_soon_filter": "Expiring in ≤ 60 days",
    "admin.title": "Administration",
    "admin.users": "Team members",
    "admin.depts": "Departments",
    "admin.new_dept": "New department",
    "admin.edit_dept": "Edit department",
    "admin.parent_dept": "Parent department",
    "admin.projects": "Projects",
    "admin.workflows": "Approval Workflows",
    "admin.integrations": "Integrations",
    // ─── Reimbursements
    "reimb.title": "Reimbursements",
    "reimb.tab_overhead": "Overhead",
    "reimb.tab_project": "Project Costs",
    "reimb.tab_approvals": "Approvals",
    "reimb.expense_type": "Expense type",
    "reimb.amount": "Amount",
    "reimb.currency": "Currency",
    "reimb.fx_converted": "Converted (today's rate)",
    "reimb.date": "Date",
    "reimb.description": "Description",
    "reimb.store": "Store / vendor",
    "reimb.supplier": "Supplier",
    "reimb.receipt": "Receipt (photo or PDF)",
    "reimb.submit": "Submit for approval",
    "reimb.project": "Project",
    "reimb.cost_code": "Cost Code",
    "reimb.item_type": "Item type",
    "reimb.material": "Material",
    "reimb.service": "Service",
    "reimb.add_item": "Add item",
    "reimb.history": "History",
    "reimb.invoice": "Invoice",
    "reimb.tip_personal": "💡 For personal expenses (meals, lodging, mileage etc.), use the Team member Expenses page with the Sagewood policy applied automatically.",
    "reimb.entries": "entries",
    "reimb.col_category": "Category",
    "reimb.col_description": "Description",
    "reimb.col_cost_code_supplier": "Cost code / Supplier",
    "reimb.col_date": "Date",
    "reimb.col_amount": "Amount",
    "reimb.col_receipt": "Receipt",
    "reimb.col_status": "Status",
    "reimb.view_receipt": "View",
    "reimb.no_history": "—",
    "reimb.tap_upload": "Tap to upload photo or PDF",
    "reimb.upload_help": "JPG, PNG, PDF, HEIC up to 10MB",
    "reimb.item_n": "Item {n}",
    "reimb.draft_returned_title": "This reimbursement was returned as draft",
    "reimb.draft_returned_help": "Adjust the data and resubmit for new approval.",
    "reimb.manager_comment": "Manager comment",
    "reimb.edit_draft": "Edit draft",
    "reimb.editing_draft": "Editing draft",
    "reimb.resubmit": "Resubmit for approval",
    "reimb.save_for_later": "Save as draft",
    "reimb.save_for_later_hint": "Save what you have so far. You can keep editing before submitting.",
    "reimb.resubmitting": "Sending...",
    "reimb.project_locked_warning": "Cost center / supplier cannot be edited here — if you need to change them, delete this draft and create a new project reimbursement. Current:",
    "reimb.invoice_label": "Invoice #",
    // ─── Batch decide
    "batch.title": "Batch decide",
    "batch.help": "Decide each item individually. Rejected items automatically spawn a new draft so the team member can fix and resubmit.",
    "batch.approve_all": "Approve all",
    "batch.reject_all": "Reject all",
    "batch.clear": "Clear",
    "batch.col_user": "Team member",
    "batch.col_description": "Description",
    "batch.col_date": "Date",
    "batch.col_amount": "Amount",
    "batch.col_decision": "Decision",
    "batch.approve": "Approve",
    "batch.reject": "Reject",
    "batch.comment_label": "Comment (optional, applied to all items in this batch)",
    "batch.comment_placeholder": "e.g. Incomplete receipts — please attach invoice",
    "batch.empty_help": "Select decisions to enable submit.",
    "batch.selected_summary": "{a} approve · {r} reject · Selected total: ${total}",
    "batch.submit": "Submit decisions ({n})",
    "batch.sending": "Sending...",
    "batch.processed_title": "Batch processed",
    "batch.processed_approved": "Approved",
    "batch.processed_rejected": "Rejected",
    "batch.processed_drafts": "Drafts created",
    "batch.processed_errors": "Errors",
    "batch.empty_state": "No pending reimbursements for approval at this time.",
    // ─── Travel
    "travel.title": "Travel",
    "travel.new": "New trip",
    "travel.purpose": "Purpose",
    "travel.destination": "Destination",
    "travel.departure": "Departure",
    "travel.return": "Return",
    "travel.notes": "Notes",
    "travel.notes_optional": "Notes (optional)",
    "travel.items": "Items",
    "travel.items_count": "Items ({n})",
    "travel.category": "Cat.",
    "travel.date": "Date",
    "travel.description": "Description",
    "travel.description_opt": "Description (optional)",
    "travel.amount": "Amount ($)",
    "travel.miles": "Miles",
    "travel.nights": "Nights",
    "travel.cost_center": "Cost center",
    "travel.fx_loading": "Fetching FX rate…",
    "travel.fx_rate_label": "rate",
    "travel.select_cost_center": "Select…",
    "travel.new_total": "New total: ${total}",
    "travel.fx_locked": "FX locked",
    "travel.original": "Original",
    "travel.request_currency": "Currency (applies to all items)",
    "travel.request_currency_help": "All items in this request will be recorded in this currency. USD equivalent is locked at approval time.",
    "travel.confirm_delete": "Delete this draft?",
    "travel.confirm_admin_delete": "This will move the request to the deleted trash. Only admins can restore it. Continue?",
    "travel.delete_draft_hint": "Delete this draft",
    "travel.admin_delete_hint": "Admin: delete this request (audit trail preserved)",
    "travel.admin_delete_btn": "Delete (admin)",
    "travel.show_deleted": "Show trash (admin)",
    "travel.restore": "Restore",
    "travel.deleted_by": "Deleted by",
    "travel.qbo_update_btn": "Update in QBO",
    "travel.qbo_update_confirm": "This will delete the current QBO Bill and recreate it with the latest data (descriptions, totals, FX). Continue?",
    "travel.qbo_update_ok": "QBO Bill refreshed with the latest data.",
    "travel.qbo_update_hint": "Push local adjustments (descriptions/FX) to QBO without reopening the approval workflow",
    "travel.total": "Total: ${total}",
    "travel.policy_warning": "Above policy limit",
    "travel.originated_banner": "This draft contains items rejected from a previous trip. Adjust and resubmit for new approval.",
    "travel.originated_count_hint": "{n} item(s) need adjustment — see the reason on each below.",
    "travel.previous_rejection": "Previous rejection reason",
    "travel.spawned_card_badge": "Rejected · Adjust",
    "travel.at_least_one_item": "At least 1 item is required.",
    "travel.alert_set_decision_all": "Set a decision for all {total} items ({missing} pending).",
    "travel.alert_draft_min": "Each item needs a date and amount to save as draft.",
    "travel.alert_resubmit_min": "Each item needs a date, amount and cost center.",
    "travel.alert_missing_receipts": "Receipt attachment is required on every item ({n} missing). Use \"Save as draft\" if you want to attach them later.",
    "travel.alert_fill_trip_fields": "Fill in purpose, destination, departure and return.",
    "travel.alert_save_draft_err": "Failed to save draft",
    "travel.alert_resubmit_err": "Failed to resubmit",
    "travel.items_with_cost_center": "Expense items (cost center per line)",
    "travel.view_receipt": "View receipt",
    "travel.empty_title": "No expenses registered yet.",
    "travel.empty_hint": "Start by creating your first travel expense.",
    "travel.create_expense": "Create expense",
    "travel.draft_owner_banner_idle": "This is your draft. You can edit items and resubmit for approval.",
    "travel.draft_owner_banner_editing": "You are editing this draft. Make adjustments and click",
    "travel.edit_draft": "Edit draft",
    "travel.cancel_edit": "Cancel edit",
    "travel.save_for_later": "Save as draft",
    "travel.save_for_later_hint": "Save what you have so far. You can edit and submit later.",
    "travel.submit_for_approval": "Submit for approval",
    "travel.page_title": "Team member Expenses",
    "travel.new_expense_title": "New travel expense",
    "travel.description_placeholder": "Description",
    "travel.sync_error": "Sync error",
    "travel.qbo_retry_sync": "Retry sync",
    "travel.qbo_resync_running": "Syncing…",
    "travel.qbo_resync_ok": "QBO sync completed successfully.",
    "travel.qbo_resync_bg": "Sync is still running in the background. Refresh the page in a moment.",
    "travel.employee_vendor": "Team member vendor",
    "travel.synced_at": "Synced at",
    "common.cost_code": "Cost code",
    "common.approval_trail": "Approval trail",
    "common.last_payment_check": "Last payment check",
    "common.qbo_not_configured": "QBO not configured",
    "common.sync_error": "Sync error",
    "common.search_placeholder": "Search…",
    "common.no_results": "No results",
    "common.write_comment": "Write a comment…",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.currency": "Currency",
    "common.delete": "Delete",
    "common.deleted": "Deleted",
    "common.edit": "Edit",
    "common.export_csv": "Export CSV",
    "common.actions": "Actions",
    "common.status": "Status",
    "common.date": "Date",
    "common.amount": "Amount",
    "common.description": "Description",
    "common.add": "Add",
    "common.loading": "Loading…",
    "admin.pto.search": "Search name or email…",
    "admin.pto.all_locations": "All locations",
    "admin.pto.all_contracts": "All contracts",
    "admin.pto.balance": "PTO balance",
    "admin.pto.vacation_balance": "Vacation balance",
    "admin.pto.expires_at": "PTO expires at",
    "admin.pto.vacation_expires_at": "Vacation expires at",
    "admin.pto.total_outstanding": "Total PTO outstanding",
    "admin.pto.total_vacation_outstanding": "Total Vacation outstanding",
    "admin.pto.pto_used_year": "PTO used this year",
    "admin.pto.vac_used_year": "Vacation used this year",
    "admin.pto.annual_days": "PTO annual days",
    "admin.pto.vacation_annual_days": "Vacation annual days",
    "admin.pto.accrual_method": "Accrual method",
    "admin.pto.probation_months": "Probation months",
    "admin.pto.pto_expiration_months": "PTO expiration (months)",
    "admin.pto.vac_expiration_months": "Vacation expiration (months)",
    "admin.pto.monthly_accrual": "Monthly accrual",
    "admin.pto.annual_lump": "Annual lump",
    "admin.pto.pto_annual_label": "PTO annual",
    "admin.pto.vac_annual_label": "Vac annual",
    "admin.pto.pto_carryover": "PTO carryover",
    "admin.pto.vac_carryover": "Vac carryover",
    "admin.costcode.search": "Search code or description…",
    "admin.costcode.code": "Code",
    "admin.supplier.search": "Search supplier…",
    "admin.supplier.all_types": "All types",
    "admin.new_employee": "New team member",
    "admin.job_title": "Job title",
    "admin.contract_type": "Contract type",
    "admin.access_profile": "Access profile",
    "admin.default_payment": "Default payment components",
    "admin.payment.amount": "Amount",
    "admin.payment.label": "Label",
    "admin.payment.per_hour": "Per hour",
    "admin.payment.per_day": "Per day",
    "admin.payment.per_month": "Per month",
    "admin.payment.no_approvers": "No approvers",
    "admin.payment.default_payment": "Default payment",
    "admin.payment.config_title": "Payment configuration",
    "admin.payment.quick_presets": "Quick presets",
    "admin.menu_item": "Menu item",
    "admin.reim_counter": "Reim counter",
    "admin.corp_notif_rules": "Corporate notification rules",
    "admin.reload_demo": "Reload rich demo data",
    "qbo.expand_all": "Expand all",
    "qbo.collapse_all": "Collapse",
    "qbo.vendor_id": "QBO Vendor Id",
    "qbo.filter_by_email": "Filter by name/email…",
    "offb.awaiting_approval": "Awaiting approval",
    "offb.exit_interview": "Exit interview",
    "offb.frankcrum_unenroll": "FrankCrum unenroll",
    "offb.docusign_term": "DocuSign termination",

    // ─── Sidebar / nav (extras)
    "nav.trainings": "Trainings",
    "nav.onboarding": "Onboarding",
    "nav.departments": "Department Hub",
    "nav.salary_matrix": "Salary Matrix",
    "nav.branding": "Branding",
    "nav.superadmin": "Super Admin · Companies",
    "nav.service_contracts": "Service Contracts",
    "ext.provider_badge": "Contractor · {name}",
    "sc.title": "Service Contracts",
    "sc.subtitle": "Cross-company contracts and contractor allocation",
    "sc.new_contract": "New contract",
    "sc.create_btn": "Create contract",
    "sc.no_contracts": "No service contracts yet. Create the first one to connect two companies.",
    "sc.provider": "Provider",
    "sc.client": "Client",
    "sc.my_role": "My company's role",
    "sc.partner_company": "Partner company",
    "sc.contract_name": "Contract name",
    "sc.code": "Code",
    "sc.description": "Description",
    "sc.start": "Start",
    "sc.end": "End",
    "sc.status_active": "Active",
    "sc.status_ended": "Ended",
    "sc.status_inactive": "Inactive",
    "sc.allocations": "Allocated people",
    "sc.allocated_n": "{n} allocated",
    "sc.add_people": "Allocate someone from my company",
    "sc.allocate": "Allocate",
    "sc.select_employee": "Select employee…",
    "sc.no_allocations": "No one allocated to this contract yet.",
    "sc.col_person": "Person (provider)",
    "sc.designated_manager": "Designated manager (client)",
    "sc.no_manager": "No designated manager",
    "sc.remove": "Remove",
    "sc.removed": "Allocation removed",
    "sc.deactivate": "Deactivate allocation",
    "sc.activate": "Reactivate allocation",
    "sc.end_contract": "End contract",
    "sc.reactivate_contract": "Reactivate contract",
    "sc.provider_hint": "Your company is the PROVIDER: you select who is allocated to this contract.",
    "sc.client_hint": "Your company is the CLIENT: you designate the manager responsible for each allocated person.",
    "sc.transfer_title": "Transfer employee to another company",
    "sc.transfer_desc": "Moves the employee and all their history (PTO, reimbursements, evaluations…) to the target company. Optionally creates their allocation on the service contract pointing to their current manager.",
    "sc.transfer_btn": "Transfer employee",
    "sc.transfer_user": "Employee",
    "sc.transfer_target": "Target company",
    "sc.transfer_contract": "Allocate on contract (optional)",
    "sc.transfer_manager": "Designated manager (optional — default: current manager)",
    "sc.transfer_confirm": "Confirm transfer",
    "sc.transfer_warning": "Warning: the employee will access the portal under the target company and will need to log in again.",
    "sc.transfer_done": "Transfer completed",
    "sc.access_title": "Multi-company access",
    "sc.access_desc": "Grant admins/HR from other companies access to THIS company. They will see a company switcher in the menu.",
    "sc.access_grant": "Grant access",
    "sc.access_email": "User email (admin/HR from another company)",
    "sc.access_revoke": "Revoke",
    "sc.access_none": "No one from outside has access to this company.",
    "sc.created": "Contract created",
    "sc.saved": "Saved",
    "sc.error": "Error saving",
    "sc.fill_required": "Fill in the required fields",
    "sc.costs": "Costs & rate",
    "sc.cost_item": "Cost item — expenses (QBO account)",
    "sc.cost_center": "Cost center — expenses",
    "sc.cost_item_short": "Cost item",
    "sc.cost_center_short": "Cost center",
    "sc.cost_item_free": "Cost item (free text — QBO not synced)",
    "sc.fee_rate_type": "Resource allocation (rate)",
    "sc.rate_none": "No rate",
    "sc.rate_hourly": "Hourly",
    "sc.rate_monthly": "Monthly",
    "sc.rate_month_short": "mo",
    "sc.rate_value": "Amount (USD)",
    "sc.fee_cost_item": "Resource cost item (optional)",
    "sc.fee_cost_center": "Resource cost center (optional)",
    "sc.fee_same_hint": "If empty, uses the expenses cost item/center.",
    "sc.saved_btn": "Save costs",
    "sc.billing_title": "Billing",
    "sc.billing_subtitle": "Approved expenses in the month + resource cost — invoice-ready",
    "sc.billing_person": "Person",
    "sc.billing_rate": "Rate",
    "sc.billing_hours": "Hours",
    "sc.billing_fee": "Resource",
    "sc.billing_reimb": "Reimbursements",
    "sc.billing_travel": "Travel",
    "sc.billing_total": "Total",
    "sc.billing_grand": "Contract total",
    "sc.billing_empty": "No allocated people or amounts in this month.",
    "sc.billing_criteria": "Criteria: expenses whose approval decision falls within the selected month + resource rate (monthly, or hours × hourly rate).",
    "approvals.billing": "Billing",
    "sc.appr_col": "Approval",
    "sc.appr_not_submitted": "Not submitted",
    "sc.appr_pending": "Awaiting manager",
    "sc.appr_approved": "Approved",
    "sc.appr_rejected": "Rejected",
    "sc.appr_stale": "Amounts changed",
    "sc.appr_submit": "Submit for approval",
    "sc.appr_submitted_n": "{n} item(s) submitted for manager approval",
    "sc.appr_requested": "Requested",
    "sc.appr_in_review": "In review",
    "nav.tagline": "People Portal",
    "nav.logout_label": "logout",

    // ─── Common (extras)
    "common.former": "Former",
    "common.uploading": "Uploading...",
    "common.update": "Update",
    "common.new": "New",
    "common.error_generic": "An error occurred",
    "common.export_pdf": "Export PDF",
    "common.download_pdf": "Download detailed PDF",
    "common.zoom_in": "Zoom in",
    "common.zoom_out": "Zoom out",
    "common.expand": "Expand",
    "common.collapse": "Collapse",
    "common.dismiss": "Dismiss",
    "common.dismiss_all": "Dismiss all",
    "common.required_mark": "Required",
    "common.role": "Role",
    "common.email": "Email",
    "common.name": "Name",
    "common.phone": "Phone",
    "common.department": "Department",
    "common.location": "Location",
    "common.manager": "Manager",
    "common.hire_date": "Hire date",
    "common.birthday": "Birthday",
    "common.bio": "Bio",
    "common.job_title": "Job title",
    "common.contract": "Contract",
    "common.password": "Password",
    "common.work_email": "Work email",
    "common.personal_email": "Personal email",
    "common.emergency_contact": "Emergency contact",
    "common.value": "Value",
    "common.category": "Category",
    "common.title": "Title",
    "common.notes": "Notes",
    "common.tags": "Tags",
    "common.active": "Active",
    "common.inactive": "inactive",
    "common.history": "History",
    "common.comment": "Comment",

    // ─── Values
    "values.quality": "Quality",
    "values.care": "Care",
    "values.commitment": "Commitment",

    // ─── Dashboard (extras)
    "dash.dismiss_all": "Dismiss all",
    "dash.dismiss_alert": "Dismiss",
    "dash.hint_pto": "PTO",
    "dash.hint_vac": "Vacation",
    "dash.hint_pending": "Pending",
    "dash.hint_inbox": "Inbox",

    // ─── Profile (extras)
    "profile.edit": "Edit profile",
    "profile.change_password": "Change password",
    "profile.payment_config": "Payment configuration",
    "profile.cost_code": "Cost Code",
    "profile.pto_balance": "PTO balance",
    "profile.vacation_balance": "Vacation balance",
    "profile.password_mismatch": "Passwords don't match",
    "profile.password_updated": "Password updated.",
    "profile.current_password": "Current password *",
    "profile.new_password": "New password (≥6 chars) *",
    "profile.confirm_password": "Confirm new password *",
    "profile.days_unit": "days",
    "profile.update_btn": "Update",

    // ─── EmployeeProfileModal
    "profile_modal.salary_history": "Salary history",
    "profile_modal.no_salary_movement": "No salary movement on record.",
    "profile_modal.out_of_band": "out of band exception",
    "profile_modal.by": "by",
    "profile_modal.former": "Former",
    "profile_modal.loading": "Loading…",

    // ─── Directory (extras)
    "dir.birthdays_title": "Birthdays",
    "dir.person_one": "person",
    "dir.person_many": "people",
    "dir.no_birthdays": "No birthdays for this month.",
    "dir.today_marker": "· TODAY 🎉",
    "dir.results": "results",
    "dir.active_tab": "Active team members",
    "dir.former_tab": "Former team members",
    "dir.former_badge": "Former",
    "dir.hired_on": "Hired",
    "dir.month_1": "January",
    "dir.month_2": "February",
    "dir.month_3": "March",
    "dir.month_4": "April",
    "dir.month_5": "May",
    "dir.month_6": "June",
    "dir.month_7": "July",
    "dir.month_8": "August",
    "dir.month_9": "September",
    "dir.month_10": "October",
    "dir.month_11": "November",
    "dir.month_12": "December",

    // ─── OrgChart (extras)
    "org.subtitle_people": "team members",
    "org.subtitle_depts": "departments",
    "org.view_tree": "Tree",
    "org.view_list": "List",
    "org.expand_all": "Expand all",
    "org.collapse_all": "Collapse all",
    "org.export_pdf": "Export PDF",
    "org.export_failed": "Failed to export PDF",
    "org.search_placeholder": "Search by name, job title, email...",
    "org.departments_title": "Departments",
    "org.head": "Head",
    "org.no_head": "No head assigned",
    "org.colab_short": "people",
    "org.report_one": "report",
    "org.report_many": "reports",

    // ─── Loans
    "loans.title": "Team member Loans",
    "loans.count_one": "loan(s)",
    "loans.active": "active",
    "loans.register_btn": "Register loan",
    "loans.register_title": "Register loan for team member",
    "loans.confidentiality": "Loans are highly confidential. Only HR can create them on behalf of a team member.",
    "loans.employee_required": "Team member *",
    "loans.select_employee": "Select team member...",
    "loans.amount_required": "Amount (USD) *",
    "loans.installments_required": "Installments (1-24) *",
    "loans.monthly_estimate": "Estimated monthly payment:",
    "loans.disbursement_date_required": "Desired disbursement date *",
    "loans.reason_required": "Reason *",
    "loans.notes_label": "Notes",
    "loans.saving": "...",
    "loans.error_select_employee": "Select the team member this loan is for.",
    "loans.label_singular": "Loan",
    "loans.agreement": "Loan Agreement",
    "loans.approved_by": "Approved by:",
    "loans.approval_chain": "Approval chain",
    "loans.payment_schedule": "Payment schedule",
    "loans.paid_label": "Paid:",
    "loans.outstanding_label": "Outstanding:",
    "loans.due": "Due",
    "loans.paid_on": "Paid",
    "loans.mark_paid": "Mark paid",
    "loans.your_approval": "Your approval ({role})",
    "loans.comment_optional": "Comment (optional)",
    "loans.reject": "Reject",
    "loans.approve": "Approve",
    "loans.mine": "Mine",
    "loans.all": "All",
    "loans.no_loans": "No loans.",
    "loans.repaid_pct": "Repaid",
    "loans.status_requested": "requested",
    "loans.status_active": "active",
    "loans.status_paid": "paid",
    "loans.status_rejected": "rejected",

    // ─── Documents (extras)
    "docs.subtitle_count": "documents",
    "docs.new_btn": "New document",
    "docs.modal_new": "New document",
    "docs.modal_edit": "Edit document",
    "docs.field_title": "Title *",
    "docs.field_description": "Description",
    "docs.field_category": "Category *",
    "docs.field_url": "Document URL or upload",
    "docs.url_placeholder": "https://... or upload below",
    "docs.upload_failed": "Upload failed:",
    "docs.delete_confirm": "Delete \"{title}\"?",

    // ─── Manager Cockpit (extras)
    "cockpit.title": "Manager Cockpit",
    "cockpit.greeting": "Hello, {name}. Your consolidated team view.",
    "cockpit.scope_direct": "Direct team",
    "cockpit.scope_extended": "Extended team",
    "cockpit.scope_direct_label": "direct",
    "cockpit.scope_extended_label": "extended",
    "cockpit.tab_overview": "Overview",
    "cockpit.tab_calendar": "Calendar",
    "cockpit.tab_timeline": "Timeline",
    "cockpit.tab_risks": "Risks",
    "cockpit.my_team": "My team",
    "cockpit.need_now": "Needs you now",
    "cockpit.items": "items",
    "cockpit.all_clear": "All clear! No pending items.",
    "cockpit.people": "People",
    "cockpit.kpi_headcount": "Headcount",
    "cockpit.kpi_pto_planned": "Planned PTO",
    "cockpit.kpi_performance": "Performance",
    "cockpit.kpi_reimb_ytd": "Reimbursements YTD",
    "cockpit.kpi_trainings": "Trainings",
    "cockpit.kpi_loans": "Loans",
    "cockpit.kpi_scope": "scope",
    "cockpit.kpi_next_90d": "next 90d · total balance {n}d",
    "cockpit.kpi_evals_90d": "{n} evaluations in 90d",
    "cockpit.kpi_pending_approval": "{n} awaiting approval",
    "cockpit.kpi_overdue_team": "{n} overdue in team",
    "cockpit.kpi_loans_sub": "awaiting your decision",
    "cockpit.urgency_high": "Urgent",
    "cockpit.urgency_normal": "Pending",
    "cockpit.urgency_soft": "Soft",
    "cockpit.col_employee": "Team member",
    "cockpit.col_dept": "Dept",
    "cockpit.col_pto_balance": "PTO Balance",
    "cockpit.col_next_vacation": "Next vacation",
    "cockpit.col_last_1on1": "Last 1:1",
    "cockpit.col_last_eval": "Last evaluation",
    "cockpit.col_trainings": "Trainings",
    "cockpit.no_employees": "No team members in scope.",
    "cockpit.overdue_short": "overdue",
    "cockpit.unavailable": "Cockpit unavailable",
    "cockpit.load_error": "Failed to load Cockpit",
    "cockpit.1on1": "1:1",
    "cockpit.next_1on1": "Next 1:1",
    "cockpit.notes_label": "Notes",
    "cockpit.notes_placeholder": "How was the conversation, key discussion points…",
    "cockpit.action_items": "Action items (one per line)",
    "cockpit.action_items_placeholder": "• Review career plan\n• Enroll in training X",
    "cockpit.save_1on1": "Save 1:1",
    "cockpit.saving_1on1": "Saving…",
    "cockpit.history": "History",
    "cockpit.next_label": "Next:",
    "cockpit.error_saving": "Error saving",


    "travel.cancel": "Cancel",
    "travel.resubmit": "Resubmit for approval",
    "travel.resubmitting": "Sending...",
    "travel.edit_form_title": "Edit draft",
    "travel.your_approval": "Your approval ({role})",
    "travel.comment_optional": "Comment (optional)",
    "travel.itemized_toggle": "Decide item by item",
    "travel.itemized_help_1": "Mark each line as",
    "travel.itemized_help_2": "or",
    "travel.itemized_help_3": "in the items list above. Rejected items will move to a new draft for the team member.",
    "travel.decided_count": "Decided: {n} / {total}",
    "travel.confirm_decisions": "Confirm {n} decision(s)",
    "travel.approve": "Approve",
    "travel.reject": "Reject",
    "travel.line_approve": "Approve",
    "travel.line_reject": "Reject",
    "travel.line_approved": "Line approved",
    "travel.line_rejected": "Line rejected",
    "travel.decided_count_drafts": "{n} approved · {r} rejected",
    "travel.spawned_draft": "Draft created for rejected items:",
    "travel.expense_items": "Expense items",
    "travel.mark_paid": "Mark as paid",
    "travel.filter_all": "all",
    "travel.filter_draft": "draft",
    "travel.filter_pending": "Pending approval",
    "travel.filter_ready": "Ready for finance",
    "travel.filter_submitted_qbo": "Submitted to QBO",
    "travel.filter_paid": "Paid",
    "travel.filter_rejected": "Rejected",
    "travel.compliance_flagged": "{n} item(s) exceed policy",
    "travel.approval_chain": "Approval chain",
    "travel.waiting_for": "Waiting for",
    "travel.cat_meals": "Meals",
    "travel.cat_lodging": "Lodging",
    "travel.cat_transport": "Transport",
    "travel.cat_mileage": "Mileage",
    "travel.cat_other": "Other",
    // ─── Approvals
    "approvals.title": "Pending approvals",
    "approvals.pto": "PTO / Vacation",
    "approvals.reimb": "Reimbursements",
    "approvals.approve": "Approve",
    "approvals.reject": "Reject",
    "approvals.comment": "Comment",
    "approvals.empty": "No pending approvals.",
    // ─── Documents / directory / org / profile
    "docs.title": "Document Center",
    "docs.search": "Search documents...",
    "directory.title": "Team member directory",
    "directory.search": "Search by name, role, department...",
    "directory.contract": "Contract",
    "directory.location": "Location",
    "directory.all": "All",
    "org.title": "Organizational Chart",
    "profile.title": "My Profile",
    // ─── Status
    "status.draft": "Draft",
    "status.pending": "Pending",
    "status.pending_approval": "Pending approval",
    "status.approved": "Approved",
    "status.rejected": "Rejected",
    "status.submitted_on_redteam": "Submitted on RedTeam",
    "status.submitted_to_qbo": "Submitted to QBO",
    "status.ready_for_finance": "Ready for Finance",
    "status.paid": "Paid",
    // ─── RedTeam / QBO sync
    "reimb.submit_redteam": "Submit on RedTeam",
    "reimb.submit_redteam_help": "Enter the RedTeam submission reference (optional).",
    "reimb.submit_redteam_ref": "RedTeam reference",
    "reimb.submit_redteam_action": "Confirm submission",
    "reimb.qbo_sync_title": "QuickBooks sync",
    "reimb.qbo_payment_status": "Payment status",
    "reimb.qbo_unpaid": "Awaiting payment",
    "reimb.qbo_paid": "Paid in QBO",
    "reimb.qbo_partial": "Partially paid",
    "reimb.qbo_synced_at": "Synced at",
    "reimb.qbo_vendor_credit": "VendorCredit",
    "reimb.qbo_bill": "Bill (team member)",
    "reimb.qbo_logs": "QBO logs",
    "reimb.qbo_retry_sync": "Retry sync",
    "reimb.qbo_resync_running": "Syncing…",
    "reimb.qbo_resync_ok": "QBO sync completed successfully.",
    "reimb.qbo_resync_bg": "Sync is still running in the background. Refresh the page in a moment.",
    "reimb.reopen_from_qbo": "Reopen (back to approved)",
    "reimb.reopen_hint": "Deletes both Bill and VendorCredit in QBO and reverts the reimbursement to Approved for correction. Only available while unpaid.",
    "reimb.reopen_confirm": "This will delete the Bill and VendorCredit in QuickBooks and revert this reimbursement to APPROVED. Continue?",
    "reimb.reopen_ok": "Reimbursement reopened. QBO records were deleted and the request is back to Approved.",
    "reimb.history_log": "Timeline",
    "reimb.dry_run_warning": "DRY-RUN MODE — QBO calls are simulated",
    // ─── QBO settings
    "qbo.conn_title": "QBO Connection for this company",
    "qbo.section_connection": "QBO Connection Setup",
    "qbo.section_vendors_title": "Vendors — official source",
    "qbo.section_vendors_desc": "Select the QBO vendors to be brought into the system (source of truth for suppliers).",
    "qbo.section_coa_title": "Chart of Accounts",
    "qbo.section_coa_desc": "Select which QBO accounts are available. The mapping to expense types is done on the Travel Policy screen.",
    "qbo.section_classes_title": "Cost Codes (QBO Classes) — official source",
    "qbo.section_classes_desc": "Select the QBO classes to be used as cost codes across the system.",
    "qbo.section_departments_title": "Departments",
    "qbo.section_departments_desc": "Link each department to its corresponding QBO Class (department becomes the source of truth).",
    "qbo.qbo_class": "QBO Class",
    "qbo.chart_of_accounts": "Chart of Accounts",
    "qbo.sync_classes_first": "Please sync QBO Classes before mapping departments.",
    "qbo.no_departments": "No departments registered yet.",
    "qbo.synced_ok": "Synced with QBO",
    "qbo.new_dept": "New department",
    "qbo.edit_dept": "Edit department",
    "qbo.parent_dept": "Parent department",
    "qbo.dept_delete_confirm": "Delete department \"{name}\"?",
    "qbo.coa_travel_hint": "Once synced, go to Travel Policy → Item to pick the specific account for each expense type.",
    "qbo.conn_desc": "Each company can use its own QuickBooks account. Credentials are tenant-isolated.",
    "qbo.conn_connected": "Connected",
    "qbo.conn_disconnected": "Not connected",
    "qbo.tenant": "Tenant",
    "qbo.realm": "Realm",
    "qbo.environment": "Environment",
    "qbo.dry_run": "Dry-run",
    "qbo.yes": "Yes",
    "qbo.no": "No",
    "qbo.btn_update": "Update credentials",
    "qbo.btn_connect": "Connect QBO",
    "qbo.btn_disconnect": "Disconnect",
    "qbo.client_id": "QBO Client ID",
    "qbo.client_secret": "QBO Client Secret",
    "qbo.realm_id": "Realm ID (Company ID)",
    "qbo.environment_label": "Environment",
    "qbo.env_production": "Production",
    "qbo.env_sandbox": "Sandbox",
    "qbo.refresh_token": "Refresh Token",
    "qbo.refresh_help": "Get these values at Intuit Developer Portal > Keys & OAuth. The Refresh Token is generated after authorizing the app in QBO.",
    "qbo.mode": "Mode",
    "qbo.btn_test": "Test connection",
    "qbo.testing": "Testing…",
    "qbo.test_ok": "Connected to",
    "qbo.broker_url_label": "Token URL (signed broker)",
    "qbo.broker_url_help": "Paste the signed URL that returns a QBO access_token (e.g. Azure Function). The code embedded in the URL is the credential — never displayed back.",
    "qbo.show_advanced": "Advanced (direct OAuth)",
    "qbo.hide_advanced": "Hide advanced",
    "qbo.advanced_hint": "Legacy mode: use only if you don't have a broker URL. Provide Client ID, Secret, Realm and Refresh Token from Intuit Developer Portal.",
    "qbo.bulk_retry": "Retry pending syncs",
    "qbo.bulk_retrying": "Retrying…",
    "qbo.bulk_retry_confirm": "This will re-sync ALL travels and reimbursements currently in error state. Continue?",
    "qbo.bulk_retry_done": "{synced} synced · {errors} errored · {skipped} skipped (out of {total} tried)",
    "qbo.btn_cancel": "Cancel",
    "qbo.btn_save": "Save connection",
    "qbo.saving": "Saving...",
    "qbo.save_success": "QBO connection saved. Next syncs will use these credentials.",
    "qbo.disconnect_confirm": "Disconnect QBO from this company? Tokens and settings will be removed. Previously-synced receipts remain.",
    "qbo.disconnected": "QBO connection removed.",
    "qbo.mapping_title": "QBO Sync & Mapping",
    "qbo.mapping_desc": "QuickBooks Online catalog + mapping for travel reimbursements.",
    "qbo.sync": "Sync",
    "qbo.card_vendors": "Vendors (QBO)",
    "qbo.card_accounts": "Accounts (Chart of Accounts)",
    "qbo.card_classes": "Classes (Projects/CC)",
    // ─── Header & misc
    "header.tour_guided": "Guided tour",
    "header.alerts": "Alerts",
    // ─── Common (extras post-iter35)
    "common.required": "Required field",
    "common.refresh": "Refresh",
    "common.search": "Search",
    "common.back": "Back",
    "common.close": "Close",
    "common.confirm": "Confirm",
    "common.optional": "optional",
    "common.yes": "Yes",
    "common.no": "No",
    "common.error": "Error",
    "common.success": "Success",
    "common.select": "Select…",
    "common.all": "All",
    "common.none": "—",
    "common.total": "Total",
    // ─── i18n audit additions (Jun 2026)
    "common.employee": "Team member",
    "common.admin": "Admin",
    "common.type": "Type",
    "common.approve": "Approve",
    "common.reject": "Reject",
    "common.submit": "Submit",
    "common.next": "Next",
    "common.view": "View",
    "common.open": "Open",
    "common.done": "Done",
    "common.clear": "Clear",
    "common.hours": "Hours",
    "common.period": "Period",
    "common.company": "Company",
    "common.total_hours": "Total hours",
    "common.nothing_export": "Nothing to export.",
    "common.upload_failed": "Upload failed:",
    "common.download_failed": "Download failed:",
    "common.delete_named": "Delete \"{title}\"?",
    "common.delete_q": "Delete?",
    "status.in_progress": "In progress",
    "status.completed": "Completed",
    "status.active": "Active",
    "status.closed": "Closed",
    "status.failed": "Failed",
    "status.open": "Open",
    "travel.scope_mine": "Mine",
    "travel.scope_all": "All",
    "travel.requests_policy": "{n} request(s) · Policy:",
    "travel.flagged_n": "{n} flagged",
    "travel.exceed_policy": "item(s) exceed policy",
    "travel.policy_alerts": "Policy alerts",
    "travel.reopen_from_qbo": "Reopen (back to draft)",
    "travel.reopen_hint": "Deletes the QBO Bill and reverts the travel to draft for correction. Only available while the Bill is unpaid.",
    "travel.reopen_confirm": "This will delete the Bill in QuickBooks and revert this travel to DRAFT. Continue?",
    "travel.reopen_ok": "Travel reopened. The QBO Bill was deleted and this record is back to draft.",
    "travel.qbo_synced": "synced",
    "travel.bill_employee": "Bill (team member)",
    "travel.per_day": "per day",
    "travel.per_night": "per night",
    "travel.per_mile": "per mile",
    "admin.tab_payroll": "Payroll Companies",
    "admin.tab_cost_codes": "Cost Codes",
    "admin.tab_suppliers": "Suppliers",
    "admin.tab_job_profiles": "Job Profiles",
    "admin.tab_access_profiles": "Access Profiles",
    "admin.tab_workflows": "Approval Workflows",
    "admin.tab_travel_policy": "Travel Policy",
    "admin.tab_demo": "Demo & Manual",
    "admin.tab_notifications": "Notification Rules",
    "admin.tab_menu_visibility": "Menu Visibility",
    "admin.tab_deploy_check": "Pre-Deploy Check",
    "admin.tab_permissions": "Permissions",
    "deploycheck.title": "Pre-deploy check",
    "deploycheck.desc": "Runs the full regression suite (security + smoke across all modules) against this Preview. Only Deploy with a GO verdict.",
    "deploycheck.run_btn": "Run check",
    "deploycheck.running": "Running…",
    "deploycheck.running_hint": "Running the test suite (~30-60s). Results will appear here automatically.",
    "deploycheck.started": "Check started",
    "deploycheck.go": "GO — safe to Deploy",
    "deploycheck.nogo": "NO-GO — do not deploy",
    "deploycheck.passed": "Passed",
    "deploycheck.failed": "Failed",
    "deploycheck.total": "Total",
    "deploycheck.skipped": "Skipped",
    "deploycheck.failures": "Failing tests",
    "deploycheck.preview_only": "Only available in the Preview environment (does not run in production).",
    "deploycheck.last_run": "Last run",
    "deploycheck.by": "by",
    "deploycheck.never_run": "No check has been run yet.",
    "deploycheck.output": "View full output",
    "admin.tab_qbo_admin": "QBO Sync & Setup",
    "admin.qbo_admin_desc": "Configure connection, QBO catalogs, departments, cost codes and suppliers in a single place.",
    "admin.qbo_section_departments": "Departments",
    "admin.qbo_section_cost_codes": "Cost Codes",
    "admin.qbo_section_suppliers": "Suppliers",
    "admin.qbo_section_qbo": "QBO — Connection & Catalogs",
    "admin.payroll_company": "Payroll Company",
    "admin.payroll_company_ph": "e.g. Sagewood Vendor, FrankCrum, Lummar",
    "admin.applicable_rule": "Applicable Rule",
    "admin.payroll_company_hint": "Leave blank to apply to all payroll companies",
    "admin.no_payroll_companies": "Register payroll companies in Rules by Location first",
    "admin.column_menu_item": "Menu item",
    "admin.column_hide_for": "Hide for {role}",
    "admin.column_shortcuts": "Shortcuts",
    "admin.saved_at": "Saved at {time}",
    "admin.save_config": "Save configuration",
    "admin.saving": "Saving…",
    "admin.role_employee": "Team member",
    "admin.role_manager": "Manager",
    "admin.role_ceo": "CEO",
    "admin.role_hr": "HR",
    "admin.employees_name": "Name",
    "admin.employees_active": "Active",
    "admin.employees_inactive": "Inactive",
    "admin.actions_edit": "Edit",
    "admin.list_view": "List",
    "admin.filter_active": "Active",
    "admin.filter_inactive": "Inactive",
    "admin.new_collab": "New team member",
    "admin.of_x_collabs": "{filtered} of {total} team members",
    "admin.expand_all": "Expand all",
    "admin.collapse_all": "Collapse all",
    "admin.direct_reports": "direct",
    "admin.total_reports": "total",
    "admin.registration_data": "Registration data",
    "admin.contact_section": "Contact",
    "admin.dates_balances": "Dates & balances",
    "admin.close_pwd": "Close",
    "admin.change_pwd": "Change password",
    "admin.save_pwd": "Save password",
    "admin.save_changes": "Save changes",
    "admin.saving_pwd": "Saving…",
    "admin.emergency_contact_ph": "Name + phone",
    "admin.save_fail": "Failed to save.",
    "admin.reset_pwd_fail": "Failed to reset password.",
    "admin.reactivated": "Team member reactivated.",
    "admin.deactivated": "Team member deactivated.",
    "admin.status_change_fail": "Failed to change status.",
    "admin.field_name": "Name",
    "admin.field_password": "Password",
    "admin.tab_positions": "Positions",
    "admin.new_position": "New Position",
    "admin.edit_position": "Edit Position",
    "admin.position_name": "Position name",
    "admin.section_position_core": "Position (core data)",
    "admin.section_employee_profile": "Team member Profile",
    "admin.section_employee_profile_desc": "Team member profile (check all that apply)",
    "admin.section_job_profile": "Job Profile",
    "admin.section_job_profile_desc": "Detailed position description and salary matrix mapping",
    "admin.section_access_profile": "Access Profile",
    "admin.section_access_profile_desc": "Access profile: visible systems, modules and folders",
    "admin.section_equipment": "Equipment",
    "admin.dimension_behavioral": "Behavioral",
    "admin.dimension_education": "Education",
    "admin.dimension_experience": "Experience",
    "admin.dimension_seniority": "Seniority",
    "admin.dimension_base": "Work base",
    "admin.responsibilities": "What the team member is responsible for",
    "admin.responsibilities_hint": "KPIs, targets and expected results",
    "admin.function_desc": "Team member's day-to-day function",
    "admin.function_desc_hint": "What is executed daily — tasks and routines",
    "admin.salary_matrix": "Salary Matrix",
    "admin.salary_matrix_none": "No matrix",
    "admin.salary_quadrants": "Applicable quadrants (multiple)",
    "admin.salary_quadrants_hint": "Click the matrix quadrants to mark them as applicable to this position",
    "admin.matrix_track": "Track",
    "admin.selected": "Selected",
    "admin.quadrants_selected": "{n} quadrant(s) selected",
    "admin.responsible_dept": "Responsible Dept",
    "admin.equipment_dept_hint": "Link each equipment to a responsible department. When hiring, an onboarding task will be created for that department.",
    "admin.policy_qbo_account": "Chart of Accounts (QBO) for this expense",
    "admin.policy_qbo_account_hint": "Every posting of this category will hit this QBO account when synced.",
    "admin.policy_qbo_no_accounts": "Sync Chart of Accounts in QBO Sync first",
    "admin.access_starter_btn": "Apply template",
    "admin.access_starter_confirm": "Replace the current tree with the default template?",
    "admin.hard_skills": "Required hard skills",
    "admin.soft_skills": "Behavioral / soft skills",
    "admin.add_custom_skill": "Add custom",
    "admin.multi_select_hint": "Multiple choice — click to toggle",
    "admin.behavioral_descriptions": "Behavioral profile descriptions",
    "admin.edit_lists": "Edit lists",
    "admin.equipment_hint": "Check the equipment this position requires",
    "admin.new_item": "New item",
    "admin.access_profile_tree": "Permission tree (Systems → Modules → Folders)",
    "admin.new_system": "New system",
    "admin.new_module": "New module",
    "admin.new_folder": "New folder",
    "admin.system_name": "System name",
    "admin.module_name": "Module name",
    "admin.folder_name": "Folder name",
    "admin.multi_payroll_companies": "Payroll Companies (multi)",
    "admin.saved": "Saved",
    "admin.rule_delete_confirm": "Delete this rule?",
    "admin.contract_optional": "Contract type (optional)",
    "admin.all_contracts": "All contracts",
    "admin.pto_carry_max": "PTO carry-over max",
    "admin.vac_carry_max": "Vacation carry-over max",
    "admin.cc_delete_confirm": "Delete this cost code?",
    "admin.new_cost_code": "New cost code",
    "admin.sup_delete_confirm": "Delete this supplier?",
    "admin.new_supplier": "New supplier",
    "admin.sup_contact_ph": "phone, email or website",
    "admin.adjust_reason_ph": "e.g. Year-end adjustment, exception approved by CEO",
    "admin.by_manager": "By manager",
    "admin.search_users_ph": "Search by name, email, job title, cost code…",
    "admin.all_roles": "All roles",
    "admin.all_depts": "All departments",
    "admin.no_users_filters": "No team members found with these filters.",
    "admin.no_changes": "No changes to save.",
    "admin.user_updated": "Team member updated.",
    "admin.pwd_min": "New password must be at least 6 characters.",
    "admin.pwd_reset_done": "Password reset. The team member must log in again (all old tokens were invalidated).",
    "admin.deactivate_confirm": "Deactivate {name}? They will lose access immediately.",
    "admin.edit_employee": "Edit team member",
    "admin.pto_balance_days": "PTO balance (days)",
    "admin.vac_balance_days": "Vacation balance (days)",
    "admin.deactivate": "Deactivate",
    "admin.reset_password": "Reset password",
    "admin.reset_pwd_hint": "Admin only. Invalidates all of the team member's old JWT tokens.",
    "admin.new_pwd_ph": "New password (min 6 chars)",
    "admin.after_create_qbo": "After creating, go to",
    "admin.after_create_qbo2": "on that row to create the Vendor in QuickBooks.",
    "admin.manager_hiring": "Manager (hiring manager)",
    "admin.new_profile": "New profile",
    "admin.no_components": "No components. Add one or pick a preset.",
    "admin.amount_usd": "Amount (USD)",
    "admin.add_component": "Add component",
    "admin.new_chain": "New chain",
    "admin.unlimited_ph": "leave blank for unlimited",
    "admin.demo_reset": "Demo Reset Script",
    "admin.slide_deck": "Slide deck for the client / new team members",
    "admin.download_pptx": "Download .PPTX",
    "admin.download_pdf": "Download .PDF",
    "admin.project_delete_confirm": "Delete project \"{code} · {name}\"? This cannot be undone.",
    "admin.new_project": "New project",
    "admin.email_transport": "Email transport:",
    "admin.pto_reminder_hours": "PTO pending reminder after (hours)",
    "admin.eval_reminder_days": "Evaluation pending reminder (days before cycle end)",
    "admin.view_outbox": "View outbox",
    "admin.new_policy": "New policy",
    "admin.policy_delete_type_confirm": "Delete type \"{cat}\"? Existing expenses are NOT deleted; the type just stops appearing for new entries.",
    "admin.delete_type": "Delete type",
    "admin.internal_key": "Internal key (lowercase, no spaces)",
    "admin.cat_label_ph": "e.g. Meals",
    "admin.calc_mode": "Calculation mode",
    "admin.calc_per_item": "Per item",
    "admin.calc_per_day": "Per day",
    "admin.calc_per_trip": "Per trip (× days)",
    "admin.policy_notes_ph": "e.g. receipt required above $50; client dinners allowed with prior approval…",
    "admin.menuvis_desc": "Sidebar menu visibility per role.",
    "admin.menuvis_hide": "hide",
    "admin.menuvis_hint": "the item for that user type. Useful for phased rollouts.",
    "admin.menuvis_check": "Check a role to",
    "admin.menuvis_admin_note": "Admins always see everything.",
    "admin.show_all": "Show for all",
    "admin.hide_all": "Hide for all",
    "pay.total_value": "Total value",
    "pay.total_due": "Total due",
    "pay.gen_invoices_confirm": "Generate invoices for {type} in period {start} → {end}?",
    "pay.payroll_type": "Payroll type",
    "pay.start": "Start",
    "pay.dist_project": "Breakdown by project (one invoice per project)",
    "pay.dist_employee": "Breakdown by team member (one invoice per PJ)",
    "pay.composition": "Payment composition",
    "pay.summary_emp": "Summary by team member",
    "pay.all_types": "All types",
    "pay.no_invoices": "No invoices generated yet.",
    "ts.subtitle": "Log your hours with allocation across projects and departments",
    "ts.period_start": "Period start",
    "ts.period_end": "Period end",
    "ts.no_rows": "No rows. Add an allocation below.",
    "ts.save_draft": "Save draft",
    "ts.submit_approval": "Submit for approval",
    "ts.history": "Timesheet history",
    "ts.no_periods": "No periods recorded.",
    "perf.subtitle": "Competency evaluations and improvement plans",
    "perf.self_ref": "Team member self-evaluation (reference)",
    "perf.tab_pending": "To evaluate (pending)",
    "perf.tab_team": "My team (all)",
    "perf.tab_all": "All (HR)",
    "perf.tab_config": "Configuration",
    "perf.new_cycle": "New cycle",
    "perf.clear_filter": "Clear filter",
    "perf.new_pip": "New PIP",
    "perf.no_pips": "No PIPs.",
    "perf.add_goal": "Add goal",
    "perf.no_followups": "No follow-ups yet.",
    "perf.close_pip": "Close PIP",
    "perf.new_followup": "New follow-up",
    "perf.new_end_date": "New end date",
    "perf.del_eval_tooltip": "Delete this evaluation",
    "perf.del_eval_confirm": "Delete {name}'s evaluation in this cycle? All progress is lost.",
    "perf.eval_deleted": "Evaluation deleted.",
    "perf.evals_created": "{added} evaluation(s) created",
    "perf.evals_skipped": "{skipped} already existed",
    "perf.add_people": "Add people to the cycle",
    "perf.cycle_draft_created": "Cycle created as draft. Go to the list to Activate.",
    "perf.cycle_name_ph": "e.g. 2026-H1 Review",
    "perf.no_groups_yet": "You haven't created groups yet. Go to",
    "perf.search_ph": "Search by name, email, job title…",
    "perf.mark_visible": "Select visible",
    "perf.role_label": "Role:",
    "perf.no_visible": "No team members visible with the current filters.",
    "perf.no_dept": "No department",
    "perf.remove_q_confirm": "Remove question \"{name}\"?",
    "perf.q_removed": "Question removed.",
    "perf.group_removed": "Group removed.",
    "perf.groups_title": "Evaluation groups",
    "perf.new_group": "New group",
    "perf.groups_hint": "e.g. Leadership, Culture, Technical. Each question belongs to 1 group.",
    "perf.no_groups": "No groups created yet.",
    "perf.new_question": "New question",
    "perf.questions_hint": "Each question is a ratable item (1-5). Add as many as you like within each group.",
    "perf.no_questions": "No questions.",
    "perf.group_name_ph": "e.g. Leadership",
    "perf.group_desc_ph": "Short description of the theme",
    "perf.q_ph": "e.g. Delegates tasks clearly",
    "perf.q_help": "Description / help",
    "perf.q_help_ph": "Optional context visible to the evaluator",
    "sal.access_denied": "Salary management is accessible only to HR, admin and CEO roles.",
    "sal.title": "Salary Matrix",
    "sal.no_matrix": "No matrix configured yet",
    "sal.create_matrix": "Create matrix",
    "sal.view_history": "View history",
    "sal.edit_matrix": "Edit salary matrix",
    "sal.edit_matrix_hint": "Configure tracks (rows), levels (columns) and salary bands per cell.",
    "sal.track_name_ph": "Track name",
    "sal.add_track": "Add track",
    "sal.level_name_ph": "Level name (e.g. I, II, Senior)",
    "sal.add_level": "Add level",
    "sal.min_ph": "min",
    "sal.max_ph": "max",
    "sal.move_queued": "Move queued for approval — next approver role: {role}. The change will only apply after the full chain approves.",
    "sal.effective_date": "Effective date",
    "sal.no_history": "No history yet.",
    "sal.edit_meta": "Edit metadata",
    "sal.edit_values": "Edit values",
    "sal.new_matrix": "New matrix",
    "sal.no_contract": "No contract set (—)",
    "sal.delete_matrix_confirm": "Delete matrix \"{name}\"? Only possible if it has no occupants.",
    "sal.all_contract_types": "all contract types",
    "sal.delete_matrix": "Delete matrix",
    "sal.salary_history": "Salary history",
    "sal.no_moves": "No moves recorded.",
    "sal.search_ph": "Search by name, job title, matrix…",
    "sal.all_roles": "All job titles",
    "sal.only_allocated": "Allocated only",
    "sal.only_unallocated": "Unallocated only",
    "sal.no_emp_filters": "No team members with these filters.",
    "sal.unallocated": "Unallocated",
    "train.mgr_suggested": "Manager suggested",
    "train.delete_confirm": "Delete training \"{name}\"?",
    "train.new": "New training",
    "train.open_course": "Open course",
    "train.mandatory_roles": "Mandatory for roles",
    "train.min_days_ph": "e.g. 90 (only after 90 days at the company)",
    "train.due_date": "Due date",
    "train.note_emp": "Note to team member (optional)",
    "train.no_assignments": "No assignments.",
    "train.no_mandatory": "No mandatory trainings configured.",
    "train.no_team": "No team members.",
    "train.subtitle": "Mandatory, suggested, and manager-assigned learning trails",
    "train.team_status": "Team Status",
    "hire.observe_ph": "What did you observe? Why this transition?",
    "hire.no_equipment": "No equipment defined",
    "hire.new_vacancy": "New vacancy",
    "hire.position_select": "Position (required)",
    "hire.custom_fill": "Custom (fill manually below)",
    "hire.picking_hint": "Picking a Position auto-fills department, title, payroll company, access profile, equipment, skills, matrix and payment.",
    "hire.job_title": "Vacancy title",
    "hire.no_locations": "Register locations in Rules by Location first",
    "hire.select_location_first": "Select a location first",
    "hire.access_profile": "Access profile",
    "hire.select": "Select",
    "hire.position_snapshot": "Position snapshot (confirm or edit)",
    "hire.position_snapshot_hint": "These fields were copied from the selected Position. Tune whatever is specific to this vacancy — the master Position record won't change.",
    "hire.chip_add": "Add",
    "hire.chip_close": "Close",
    "hire.chip_new_option_ph": "New option…",
    "hire.change_stage": "Change stage with comment",
    "hire.move_stage_title": "Move candidate stage",
    "hire.new_stage": "New stage",
    "hire.tab_scoreboard": "Scoreboard",
    "hire.tab_pipeline": "Pipeline",
    "hire.tab_vacancies": "Vacancies",
    "hire.filter_status": "Status",
    "hire.filter_department": "Department",
    "hire.filter_vacancy": "Vacancy",
    "hire.filter_position": "Position",
    "hire.filter_priority": "Priority",
    "hire.filter_all": "All",
    "hire.filter_search_candidate": "Search candidate by name…",
    "hire.reset_filters": "Reset filters",
    "hire.funnel_empty_stage": "Empty",
    "hire.edit_vacancy": "Edit",
    "hire.delete_vacancy": "Delete",
    "hire.delete_confirm": "Are you sure you want to delete this vacancy? This cannot be undone.",
    "hire.no_candidates_filter": "No candidates match these filters.",
    "hire.candidates_shown": "{shown} of {total} candidates",
    "hire.scoreboard_title": "Recruitment Scoreboard",
    "hire.scoreboard_hint": "Consolidated view of vacancies, candidates and pipeline performance.",
    "hire.kpi_open_vacancies": "Open vacancies",
    "hire.kpi_active_candidates": "Active candidates",
    "hire.kpi_hired": "Hired",
    "hire.kpi_offer_acceptance": "Offer acceptance",
    "hire.by_stage": "By stage",
    "hire.avg_days_in_stage": "Avg days in stage",
    "hire.top_interviewers": "Top interviewers",
    "hire.vacancy_leaderboard": "Vacancies with most candidates",
    "hire.approved_of_assigned": "{approved} approved / {assigned} assigned",
    "hire.stage_approved": "Approved",
    "hire.assigned_interviewer": "Assigned interviewer",
    "hire.pick_interviewer": "Select an interviewer",
    "hire.pick_interviewer_required": "You must pick an interviewer before moving to Interview.",
    "hire.only_assigned_can_move": "Only the assigned interviewer can move this candidate.",
    "hire.forward_to_interviewer": "Forward to another interviewer",
    "hire.move_to_approved": "Send to Approved",
    "hire.approve_title": "Confirm position information",
    "hire.approve_hint": "Confirm the data that will be locked into the Offer Letter and the team member profile. Adjust anything that was negotiated.",
    "hire.confirm_approval": "Confirm approval",
    "hire.negotiated_salary": "Negotiated salary",
    "hire.salary_pick_cell": "Pick a matrix cell (Track × Level)",
    "hire.salary_within_band": "Within band",
    "hire.salary_outside_band": "Outside band {min}–{max}",
    "hire.confirmed_offer": "Confirmed offer",
    "hire.send_offer_letter": "Send Offer Letter",
    "hire.mark_offer_signed": "Mark as Offer Signed",
    "hire.approved_summary": "Approval summary",
    "hire.reassign_interviewer_hint": "Once approved, HR will pick this candidate up and send the Offer Letter.",
    "hire.payment_format": "Payment format",
    "hire.payment_amount": "Amount",
    "hire.allowed_band": "Allowed salary band",
    "hire.quadrants_short": "quadrant(s)",
    "hire.out_of_range": "Outside the {min}–{max} range defined by the matrix for the selected quadrants",
    "hire.job_desc": "Job description",
    "hire.create_draft": "Create draft",
    "hire.job_profile_opt": "Apply a standard job profile (optional)",
    "hire.reports_to": "Reports to (Manager)",
    "hire.target_start": "Target start date",
    "hire.new_applicant": "New applicant",
    "hire.hire_create": "Hire & create user",
    "hire.initial_pwd_ph": "Initial password",
    "hire.add_candidate": "Add candidate",
    "hire.equipment_needed": "Required equipment",
    "hire.title": "Hiring & Admission",
    "offb.finalize_confirm": "Finalize? User will be deactivated.",
    "offb.revoke_it": "Revoke IT access",
    "offb.finalize_btn": "Finalize termination & deactivate user",
    "offb.primary_reason": "Primary reason for leaving",
    "offb.new_termination": "New termination",
    "offb.none": "No terminations.",
    "onb.no_pending": "No pending onboarding",
    "onb.no_tasks": "You don't have any onboarding tasks assigned.",
    "onb.required_steps": "Required steps",
    "onb.mark_done": "Mark done",
    "kn.upload_hint": "PDF, DOCX, TXT, MD. Max 50 MB.",
    "kn.paste_text_ph": "Optional: paste extracted text here so Sage can search this document content",
    "kn.sharepoint_ph": "SharePoint site name (optional)",
    "kn.paste_summary_ph": "Paste/summarize document content here for Sage's knowledge",
    "kn.active_visible": "Active (visible to Sage Copilot)",
    "kn.subtitle": "Company documents and policies powering Sage Copilot",
    "kn.new_doc": "New document",
    "kn.search_ph": "Search docs, tags, content...",
    "kn.no_docs": "No documents found.",
    "kn.total_questions": "Total Questions",
    "kn.open_gaps": "Open Gaps",
    "kn.key_saved": "Saved. Sage will now use your custom key.",
    "kn.key_reset_confirm": "Reset to Emergent Universal Key?",
    "kn.reset_emergent": "Reset to Emergent",
    "plan.subtitle": "Your agenda, deadlines and reminders in one place",
    "plan.new_reminder": "New reminder",
    "plan.view": "View:",
    "plan.list": "List",
    "plan.search_ph": "Search by name…",
    "plan.all_employees": "All team members",
    "plan.no_events": "No events for this period.",
    "plan.delete_reminder": "Delete this reminder?",
    "plan.cycle": "Cycle:",
    "plan.for_employee": "For team member (optional)",
    "dh.title": "Department Hub",
    "dh.subtitle": "Diagrams and onboarding quizzes for every team",
    "dh.no_diagram": "No diagram yet",
    "dh.not_configured": "Not configured",
    "dh.no_diagram_dept": "No diagram has been configured for this department.",
    "dh.edit_diagram": "Edit diagram",
    "dh.edit_quiz": "Edit quiz",
    "dh.quiz_min": "Add at least one valid question with 2+ options.",
    "dh.question_ph": "Question text",
    "dh.add_option": "Add option",
    "dh.add_question": "Add question",
    "cop.feedback_ph": "What was wrong with this answer? (helps HR fix the knowledge gap)",
    "cop.delete_conv": "Delete this conversation?",
    "cop.subtitle": "Your AI assistant for company processes and policies",
    "cop.new_chat": "New chat",
    "cop.no_convs": "No conversations yet.",
    "docs.upload_url": "Document URL or upload",
    "man.sec_workspace_desc": "Your day-to-day tools",
    "man.sec_people_desc": "Know and manage your team",
    "man.sec_ops_desc": "Approve, look up, pay",
    "man.sec_system_desc": "Customisation and profile",
    "pto.no_comments": "No comments yet. Start the discussion below.",
    "reimb.select_project": "Select project...",
    "reimb.select_cost_code": "Select cost code...",
    "reimb.select_supplier": "Select supplier...",
    "reimb.employee_vendor_qbo": "Employee vendor (QBO)",
    "reimb.no_qbo_log": "No QBO log entries.",
    "reimb.date_value_required": "Date and amount are required.",
    "reimb.request_currency": "Currency (applies to all items)",
    "reimb.request_currency_help": "All items in this submission will be recorded in this currency.",
    "reimb.confirm_delete": "Delete this draft?",
    "reimb.confirm_admin_delete": "This will move the reimbursement to the trash. Only admins can restore it. Continue?",
    "reimb.admin_delete_btn": "Delete (admin)",
    "reimb.show_deleted": "Show trash (admin)",
    "reimb.next_step_redteam": "Next step: RedTeam",
    "reimb.redteam_after": "After confirming, the system triggers the QuickBooks integration automatically.",
    "qbo.search_catalog_ph": "Search (name, type, subtype)…",
    "qbo.search_ph": "Search…",
    "qbo.create_vendors_confirm": "Create QBO vendors for ALL employees still missing qbo_vendor_id?",
    "qbo.sync_result": "Synced: {ok} · Failed: {fail}",
    "qbo.display_name": "Display name",
    "qbo.categories_coa": "Categories (Chart of Accounts)",
    "brand.primary": "Primary",
    "brand.secondary": "Secondary",
    "brand.success": "Success",
    "brand.logo_mark": "Logo mark (square icon)",
    "brand.google_fonts": "Loaded via Google Fonts. For a custom font, contact the Super Admin.",
    "brand.button": "Button",
    "brand.approved": "Approved",
    "brand.pending": "Pending",
    "brand.rejected": "Rejected",
    "brand.updated": "Branding updated and applied.",
    "brand.url_ph": "URL (or upload)",
    "brand.too_big": "File larger than 10MB",
    "brand.uploaded": "Logo uploaded",
    "sa.list_fail": "Failed to list tenants",
    "sa.subtitle": "Manage tenants, branding and users for each company.",
    "sa.users_col": "Users",
    "sa.no_companies": "No companies yet.",
    "sa.delete_company": "Delete company",
    "sa.irreversible": "This action is irreversible.",
    "sa.company_label": "Company:",
    "sa.users_delete": "Users to be deleted:",
    "sa.type_slug": "To confirm, type the slug:",
    "sa.type_exact": "Type \"{slug}\" exactly to confirm",
    "sa.company_deleted": "Company \"{name}\" deleted",
    "sa.new_company": "New company",
    "sa.slug_hint": "Lowercase letters, numbers, dashes. Used internally, can't be changed later.",
    "sa.slug_name_required": "Slug and name are required",
    "sa.company_created": "Company created",
    "sa.company_updated": "Company updated",
    "sa.fill_admin": "Fill in email, name and password",
    "sa.admin_created": "Admin created: {email}",
    "sa.company_active": "Company active (uncheck to suspend access for everyone)",
    "sa.font_hint": "Font must be available via Google Fonts (already loaded). For custom fonts, contact support.",
    "sa.create_admin": "Create 1st Admin",
    "sa.action_button": "Action button",
    "cockpit.ev_vacation": "Vacation",
    "cockpit.ev_birthday": "Birthday",
    "cockpit.loading_calendar": "Loading calendar…",
    "cockpit.no_events_120": "No events in the next 120 days.",
    "cockpit.loading_timeline": "Loading timeline…",
    "cockpit.no_activity_60": "No team activity in the last 60 days.",
    "cockpit.critical": "Critical",
    "cockpit.attention": "Attention",
    "cockpit.loading_risk": "Loading risk indicators…",
    "admin.edit_cost_code": "Edit cost code",
    "admin.edit_supplier": "Edit supplier",
    "admin.no_policies": "No policies yet — click \"New policy\" to start.",
    "admin.edit_project": "Edit project",
    "admin.no_projects": "No projects yet — click \"New project\" to create one.",
    "admin.add_approver": "+ Add approver role…",
    "admin.tiers": "Tiers",
    "admin.account_status": "Account status",
    "admin.active_login": "Active · can log in",
    "admin.deactivated_login": "Deactivated · login blocked, JWTs revoked on next request",
    "admin.reactivate": "Reactivate",
    "admin.creating": "Creating...",
    "admin.create_employee": "Create team member",
    "admin.qbo_hint_click": "and click",
    "admin.qbo_hint_path": "QBO Sync & Mapping → Employees",
    "admin.corp_notif_desc": "These rules apply company-wide. HR can adjust thresholds and toggle the master email switch.",
    "admin.enabled_stub": "ENABLED (stub)",
    "admin.disabled_label": "DISABLED",
    "admin.email_note_title": "Note:",
    "admin.email_note": "Email provider (Resend/SendGrid) is not yet wired. The rule engine and outbox are functional; enabling \"Send emails\" will write computed messages to the audit outbox. When you choose a provider, we'll plug it in without any other change to these rules.",
    "admin.would_send": "message(s) would be sent. Send emails:",
    "admin.outbox_empty": "No items in outbox yet. Run with \"Send emails\" enabled to populate.",
    "admin.menuvis_reload": "After saving, ask users to reload the page (or they'll see the change on next login).",
    "travel.new_expense": "New expense",
    "qbo.coa_hint1": "Select the QBO Chart of Accounts account (or sub-account) for each category. When the Bill is sent, the line goes as",
    "qbo.coa_hint2": "pointing to the",
    "qbo.coa_hint3": "chosen. If a category has no mapping, we fall back to the default expense account.",
    "status.on_track": "On track",
    "status.needs_improvement": "Needs improvement",
    "status.off_track": "Off track",
    "perf.your_rating": "Your rating",
    "perf.rating_scale": "1=Below · 3=Meets · 5=Exceeds",
    "sal.awaiting": "Awaiting decision from",
    "sal.applicability": "Applicability",
    "sal.applies_all_1": "Applies to",
    "pay.close": "Close",
    "hire.stage_applied": "Applied",
    "hire.stage_screening": "Screening",
    "hire.stage_interview": "Interview",
    "hire.stage_offer_sent": "Offer Sent",
    "hire.stage_offer_signed": "Offer Signed",
    "hire.stage_hired": "Hired",
    "hire.stage_rejected": "Rejected",
    "hire.move_to": "Move to",
    "train.type_mandatory": "Mandatory",
    "train.type_suggested": "Suggested",
    "train.mandatory_for": "Mandatory for",
    "train.cat_compliance": "Compliance",
    "train.cat_technical": "Technical",
    "train.cat_leadership": "Leadership",
    "train.cat_safety": "Safety",
    "train.cat_soft_skills": "Soft skills",
    "train.cat_onboarding": "Onboarding",
    "train.cat_other": "Other",
    "cockpit.ev_return": "Return",
    "cockpit.ev_anniversary": "Work anniversary",
    "cockpit.info_label": "Info",
    "kn.sessions": "Sessions",
    "kn.satisfaction": "Satisfaction Rate",
    "kn.verified_qa": "Verified Q&As",
    "kn.thumbs_up": "👍 Thumbs Up",
    "kn.thumbs_down": "👎 Thumbs Down",
    "kn.storage_hint": "Local storage (will move to S3/GCS later).",
    "perf.submitted_compare": "Submitted {date}. Compare your scores below with the self-evaluation shown next to each competency.",
    "perf.click_fill_self": "→ Click to fill your self-evaluation",
    "offb.open_vacancy": "Open replacement vacancy",
    "offb.submit_request": "Submit request",
    "offb.requested_by": "Requested by",
    "offb.would_recommend": "Would recommend Sagewood to others",
    "offb.feedback_sug": "Feedback / suggestions",
    "offb.cases_summary": "{n} cases · {m} in progress",
    "offb.revoked": "Revoked ·",
    "onb.optional": "Optional / recommended",
    "onb.auto_completes": "auto-completes when done",
    "sal.target_cell": "Target cell",
    "sal.select_cell": "— select cell —",
    "sal.band_hint": "Band: {min} – {max} · target bonus {pct}% (set by the cell)",
    "sal.new_salary": "New salary",
    "sal.out_of_band": "Salary {v} is OUTSIDE the band {min}–{max}. Confirm exception.",
    "train.title": "Training & Development",
    "train.tab_mine": "My trainings",
    "train.tab_catalog": "Catalog",
    "train.tab_compliance": "Compliance",
    "train.assign": "Assign",
    "train.assign_title": "Assign:",
    "travel.receipt_required": "A receipt attachment is required to submit for approval.",
    "travel.attach_receipt": "Attach receipt",
    "travel.tap_upload": "Tap to upload photo or PDF",
    "travel.qbo_sync_title": "QuickBooks Sync",
    "travel.paid_qbo": "Paid in QBO",
    "travel.partial": "Partial",
    "travel.awaiting_payment": "Awaiting payment",
    "travel.lines": "Lines",
    "travel.dry_run_note": "DRY-RUN MODE — QBO CALLS SIMULATED",
    "perf.questions_by_group": "Questions by group",
    "perf.edit_group": "Edit group",
    "perf.edit_question": "Edit question",
    "perf.group_created": "Group created.",
    "perf.group_updated": "Group updated.",
    "perf.q_created": "Question created.",
    "perf.q_updated": "Question updated.",
    "perf.q_comp_label": "Question / competency",
    "perf.applies_roles": "Applies to roles (leave empty = all)",
    "common.order": "Order",
    "common.saving": "Saving…",
    "common.birth_date": "Birth date",
    "common.choose_file": "Choose file",
    "offb.click_toggle": "Click a task to toggle between done ↔ pending.",
    "offb.title": "Offboarding",
    "approvals.waiting_n": "{n} awaiting",
    "sal.applies_all_badge": "applies to all contracts",
    "sal.employees_count": "Team members · {a} of {b}",
    "sal.people_n": "{n} person(s)",
    "sal.new_matrix_full": "New salary matrix",
    "sal.register": "Create",
    "sal.currency": "Currency",
    "sal.active_picker": "Active (appears in picker)",
    "sal.matrix_name_ph": "e.g. Sagewood · Brazil (PJ)",
    "pay.value": "Amount",
    "pay.end": "End",
    "pay.project": "Project",
    "kn.stats": "Stats",
    "admin.policy_subtitle": "Set limits per travel expense category. Only the active policy is applied to team member submissions.",
    "admin.only_admin": "(only Admin can change)",
    "admin.configure_soon": "Configure (coming soon)",
    "qbo.save_selection": "Save selection",
    "qbo.no_match_search": "No accounts match the search.",
    "qbo.no_accounts_synced": "No accounts synced yet — click Sync in the card above.",
    "qbo.last_sync": "Last sync:",
    "qbo.never_synced": "Never synced",
    "qbo.save_mapping": "Save mapping",
    "qbo.sync_first": "Sync Categories (Chart of Accounts) and Classes above before configuring the mapping.",
    "qbo.cat_to_account": "Expense category → QBO Account",
    "qbo.not_mapped": "— not mapped —",
    "qbo.employees_to_vendor": "Employees → QBO Vendor",
    "qbo.not_synced": "— not synced —",
    "qbo.sync_all": "Sync all",
    "qbo.syncing": "Syncing…",
    "qbo.categories": "Categories",
    "brand.subtitle": "Customize logo, color palette, tagline and font. Changes reflect across the entire portal immediately.",
    "brand.palette": "Color palette",
    "brand.logo_font": "Logo · Font",
    "brand.font": "Font",
    "brand.live_preview": "Live preview",
    "perf.questions_n": "{n} question(s)",
    "perf.questions_selected": "{n} question(s) selected",
    "perf.remove_group_confirm": "Remove \"{name}\"? The {count} question(s) in this group will become ungrouped (you can reassign them later).",
    "sal.all_chip": "all",
    "qbo.depara_title": "Mapping — Travel expense → QBO",
    "qbo.dept_to_class": "Department (Cost Center) → QBO Class",
    "qbo.employees_n": "{n} employees",
  },
};

const I18nContext = createContext(null);

/**
 * Simple {placeholder} interpolation, e.g. t("dash.more_alerts", { n: 5 }).
 * Falls back to the key itself when the lookup fails, which makes missing keys
 * visible in dev without crashing the UI.
 */
function interpolate(template, params) {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (params[k] !== undefined ? String(params[k]) : `{${k}}`));
}

export function tStatic(key, params) {
  const lang = localStorage.getItem("sw_lang") || "pt";
  const raw = dict[lang]?.[key] ?? key;
  return params ? interpolate(raw, params) : raw;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("sw_lang") || "pt");
  const setLang = useCallback((l) => {
    localStorage.setItem("sw_lang", l);
    setLangState(l);
  }, []);
  const t = useCallback((key, params) => {
    const raw = dict[lang]?.[key];
    if (raw === undefined) {
      // dev hint: log missing keys (silently in prod)
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[i18n] missing key:", key, "(lang=", lang, ")");
      }
      return key;
    }
    return params ? interpolate(raw, params) : raw;
  }, [lang]);
  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
